import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle,
  Search,
  Pin,
  User,
  Phone,
  Mail,
  ShoppingBag,
  Package,
  Send,
  Paperclip,
  Smile,
  MoreVertical,
  Check,
  CheckCheck,
  Clock,
  X,
  ChevronDown,
  Filter,
  UserPlus,
  Plus,
  Settings,
  Tag,
  RefreshCw,
  Loader2,
  Image as ImageIcon,
  FileText,
  Mic,
  Star,
  Archive,
  Circle,
  File,
  Video,
  ArrowLeft,
  AlertTriangle,
  Timer,
  Instagram,
  Reply,
  Copy,
  Square,
  StopCircle,
  Play,
  Pause,
  Sparkles,
  ShoppingCart,
  Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { flagForRecord } from '@/lib/countryFlags';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import ReplySuggestionPanel from '../components/ReplySuggestionPanel';
import AICopilotInboxExtras from '../components/AICopilotInboxExtras';
import AICopilotSession6Panel from '../components/AICopilotSession6Panel';
import AICopilotHandoffBanner from '../components/AICopilotHandoffBanner';
import AICopilotComposerTools from '../components/AICopilotComposerTools';
import BurningLeadsButton from '../components/BurningLeadsButton';
import { useSettings } from '../context/SettingsContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to construct media URLs
const getMediaUrl = (mediaUrl) => {
  if (!mediaUrl) return '';
  if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) {
    return mediaUrl;
  }
  // For paths like /api/uploads/whatsapp/file.jpg, prepend the backend base URL
  if (mediaUrl.startsWith('/api/')) {
    return `${BACKEND_BASE_URL}${mediaUrl}`;
  }
  // For paths like /uploads/whatsapp/file.jpg, prepend backend + /api
  if (mediaUrl.startsWith('/')) {
    return `${API_BASE_URL}${mediaUrl}`;
  }
  // For relative paths, prepend backend + /api/
  return `${API_BASE_URL}/${mediaUrl}`;
};

// Image attachment thumbnail — fetches the image as an authenticated blob
// and renders a WhatsApp-style preview; click opens full size.
const EmailAttachmentThumb = ({ messageId, index, att }) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let url = null;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/email/messages/${messageId}/attachments/${index}`, {
          headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
        });
        url = URL.createObjectURL(res.data);
        if (!cancelled) setSrc(url); else URL.revokeObjectURL(url);
      } catch (e) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [messageId, index]);

  if (failed) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); openEmailAttachment(messageId, index, att); }}
        className="bg-black/10 hover:bg-black/25 px-1.5 py-0.5 rounded text-xs underline decoration-dotted underline-offset-2 cursor-pointer transition-colors"
        data-testid={`email-attachment-${messageId}-${index}`}
      >
        📎 {att.filename}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); openEmailAttachment(messageId, index, att); }}
      className="relative group rounded-lg overflow-hidden border border-black/15 hover:border-black/40 transition-colors"
      title={att.filename}
      data-testid={`email-attachment-thumb-${messageId}-${index}`}
    >
      {src ? (
        <img src={src} alt={att.filename} className="w-24 h-24 object-cover" loading="lazy" />
      ) : (
        <div className="w-24 h-24 bg-black/10 animate-pulse" />
      )}
      <span className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">
        {att.filename}
      </span>
    </button>
  );
};

// Fetch an email attachment as an authenticated blob and open (images /
// PDFs in a new tab) or download it (everything else).
const openEmailAttachment = async (messageId, index, att) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API_BASE_URL}/email/messages/${messageId}/attachments/${index}`, {
      headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
    });
    const blob = new Blob([res.data], { type: att.content_type || res.data.type || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const viewable = (att.content_type || '').startsWith('image/') || att.content_type === 'application/pdf';
    if (viewable) {
      window.open(url, '_blank');
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error(e.response?.status === 404
      ? 'Attachment content is no longer available for this email'
      : 'Failed to open attachment');
  }
};

// Email body renderer.
//
// IMAP gives us either real HTML (multipart/alternative text/html part) or
// plain text wrapped into the `body_html` field. When merchants reply to a
// Shopify order confirmation, the original email's lines come through with
// `>` quote prefixes (the universal mail-reply convention) — rendering that
// raw makes the conversation panel look like a wall of dangling `>` chars.
//
// Strategy:
//   1. If the body looks like real HTML (has `<p>` / `<div>` / `<a>` tags),
//      render via dangerouslySetInnerHTML untouched — do nothing clever.
//   2. Otherwise treat as plain text: collapse contiguous `>`-prefixed line
//      blocks into a "Show quoted text" toggle (Gmail style), strip the
//      prefixes, and render the rest as preformatted text.
const EmailBody = ({ body, isOutbound, message }) => {
  const [showQuoted, setShowQuoted] = useState(false);
  // Resolve inline `cid:` image references against the message's
  // attachments — browsers can't fetch cid: URLs, so we pull each
  // referenced attachment as an authenticated blob and swap the src.
  const [resolvedBody, setResolvedBody] = useState(body);
  useEffect(() => {
    setResolvedBody(body);
    const atts = message?.email_metadata?.attachments;
    if (!body || !atts?.length || !/cid:/i.test(body)) return;
    const cids = [...new Set([...body.matchAll(/src=["']cid:([^"']+)["']/gi)].map(m => m[1]))];
    if (!cids.length) return;
    let cancelled = false;
    const urls = [];
    (async () => {
      let html = body;
      const token = localStorage.getItem('token');
      for (const cid of cids) {
        const idx = atts.findIndex(a =>
          (a.content_id && (a.content_id === cid || cid.includes(a.content_id) || a.content_id.includes(cid))) ||
          (a.filename && (a.filename.toLowerCase() === cid.toLowerCase() || cid.toLowerCase().startsWith(a.filename.toLowerCase())))
        );
        if (idx === -1) continue;
        try {
          const res = await axios.get(`${API_BASE_URL}/email/messages/${message.id}/attachments/${idx}`, {
            headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
          });
          const url = URL.createObjectURL(res.data);
          urls.push(url);
          html = html.split(`cid:${cid}`).join(url);
        } catch (e) { /* leave the cid ref — image shows broken but rest renders */ }
      }
      if (!cancelled) setResolvedBody(html);
    })();
    return () => { cancelled = true; urls.forEach(u => URL.revokeObjectURL(u)); };
  }, [body, message]);

  if (!body) return null;

  // Heuristic — does the body contain real HTML markup? We check for tags
  // beyond just <br>. Plain-text bodies sometimes get wrapped in <pre> or
  // <p> by the IMAP normaliser; treat those as plain text too.
  const looksLikeHtml = /<(div|table|td|tr|p|a|span|img|ul|ol|li|h[1-6]|blockquote|strong|em|b|i)\b/i.test(body);

  if (looksLikeHtml) {
    return (
      <div
        className={`text-sm mt-1 email-body-content ${isOutbound ? 'text-black/80' : 'text-wt-text-primary'}`}
        dangerouslySetInnerHTML={{ __html: resolvedBody }}
        style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'hidden', wordBreak: 'break-word' }}
        data-testid="email-body-html"
      />
    );
  }

  // Plain-text path. Split lines, then walk forward and split into a
  // visible block (everything before the first quoted run starts) and an
  // optional quoted block (the contiguous tail of `>`-prefixed lines).
  const lines = body.split(/\r?\n/);
  const stripQuote = (l) => l.replace(/^>+\s?/, '');
  // Find the LAST contiguous run of quoted lines anchored to the end of
  // the body — that's the original email being replied to. Lines before
  // it are the merchant's actual reply + signature.
  let quotedStart = lines.length;
  // Walk backwards skipping empty lines, then keep walking while we see
  // `>` prefixes (with one-line gaps allowed for blank lines inside the
  // quote block).
  let i = lines.length - 1;
  while (i >= 0 && lines[i].trim() === '') i--;
  if (i >= 0 && /^>/.test(lines[i])) {
    while (i >= 0 && (/^>/.test(lines[i]) || lines[i].trim() === '')) {
      quotedStart = i;
      i--;
    }
    // Trim leading blanks of the quoted block.
    while (quotedStart < lines.length - 1 && lines[quotedStart].trim() === '') quotedStart++;
  }

  const visibleText = lines.slice(0, quotedStart).join('\n').replace(/\s+$/, '');
  const quotedText = lines.slice(quotedStart).map(stripQuote).join('\n').replace(/^\s+|\s+$/g, '');

  return (
    <div
      className={`text-sm mt-1 ${isOutbound ? 'text-black/80' : 'text-wt-text-primary'}`}
      style={{ maxHeight: '500px', overflow: 'auto', wordBreak: 'break-word' }}
      data-testid="email-body-text"
    >
      {visibleText && (
        <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
          {visibleText}
        </div>
      )}
      {quotedText && (
        <div className="mt-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowQuoted((v) => !v); }}
            className={`text-xs px-2 py-1 rounded inline-flex items-center gap-1 transition-colors ${
              isOutbound
                ? 'bg-black/10 text-black/60 hover:bg-black/20'
                : 'bg-wt-paper text-wt-text-muted hover:text-wt-text-primary border border-wt-border'
            }`}
            data-testid="email-toggle-quoted"
          >
            <MoreVertical className="h-3 w-3" />
            {showQuoted ? 'Hide quoted text' : 'Show quoted text'}
          </button>
          {showQuoted && (
            <div
              className={`mt-2 pl-3 border-l-2 text-xs opacity-80 ${
                isOutbound ? 'border-black/20' : 'border-wt-border'
              }`}
              style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
              data-testid="email-quoted-block"
            >
              {quotedText}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to render text with clickable links
const renderTextWithLinks = (text) => {
  if (!text) return null;
  
  // URL regex pattern
  const urlPattern = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  
  const parts = text.split(urlPattern);
  
  return parts.map((part, index) => {
    if (urlPattern.test(part)) {
      // Reset lastIndex for the regex
      urlPattern.lastIndex = 0;
      return (
        <a 
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 underline hover:text-blue-300 break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

// Common emojis for quick access
const EMOJI_LIST = [
  '😀', '😊', '😂', '🤣', '😍', '🥰', '😘', '😎', '🤔', '😅',
  '👍', '👎', '👏', '🙏', '🤝', '💪', '✌️', '🤞', '👋', '🙌',
  '❤️', '💕', '💖', '💯', '✅', '⭐', '🔥', '🎉', '🎊', '💐',
  '📦', '🚚', '🏠', '💳', '💰', '🛒', '🛍️', '📱', '💼', '📝'
];

// WhatsApp-style Audio Player Component
const AudioPlayer = ({ src, isOutbound }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoaded(true);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    const newTime = percent * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleError = () => {
    setError(true);
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  if (error) {
    return (
      <div className="flex items-center gap-3 min-w-[200px]">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isOutbound ? 'bg-black/20' : 'bg-wt-paper'}`}>
          <Mic className="h-6 w-6 text-gray-400" />
        </div>
        <div className="flex-1">
          <p className="text-xs text-gray-500">Audio failed to load</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 min-w-[200px]">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={handleError}
        preload="metadata"
      />
      
      {/* Play/Pause Button - Larger and more touchable */}
      <button
        onClick={togglePlay}
        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
          isOutbound 
            ? 'bg-black/20 hover:bg-black/30 text-black' 
            : 'bg-wt-gold/20 hover:bg-wt-gold/30 text-wt-gold'
        }`}
        data-testid="audio-play-btn"
      >
        {isPlaying ? (
          <Pause className="h-6 w-6" />
        ) : (
          <Play className="h-6 w-6 ml-0.5" />
        )}
      </button>
      
      {/* Progress and Duration */}
      <div className="flex-1 min-w-[120px]">
        {/* Progress Bar - Clickable */}
        <div 
          className={`h-1.5 rounded-full cursor-pointer ${isOutbound ? 'bg-black/20' : 'bg-wt-border'}`}
          onClick={handleSeek}
        >
          <div 
            className={`h-full rounded-full transition-all ${isOutbound ? 'bg-black/60' : 'bg-wt-gold'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Time Display */}
        <div className={`flex justify-between mt-1 text-xs ${isOutbound ? 'text-black/70' : 'text-wt-text-muted'}`}>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
      
      {/* Voice message icon */}
      <div className={`flex-shrink-0 ${isOutbound ? 'text-black/50' : 'text-wt-text-muted'}`}>
        <Mic className="h-4 w-4" />
      </div>
    </div>
  );
};

// Status colors
const STATUS_COLORS = {
  open: 'bg-green-500',
  pending: 'bg-yellow-500',
  resolved: 'bg-blue-500',
  closed: 'bg-gray-500'
};

// Tag colors
const TAG_COLORS = {
  vip: 'bg-purple-500',
  returning: 'bg-green-500',
  new: 'bg-blue-500',
  pending_payment: 'bg-yellow-500',
  complaint: 'bg-red-500',
  inquiry: 'bg-cyan-500'
};

// WhatsApp 24-Hour Window Timer Component
const WhatsAppWindowTimer = ({ whatsappWindow, compact = false }) => {
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  useEffect(() => {
    if (!whatsappWindow) return;
    
    // Update timer every minute
    const updateTimer = () => {
      if (!whatsappWindow.window_expires_at) {
        setTimeRemaining(null);
        return;
      }
      
      const expiresAt = new Date(whatsappWindow.window_expires_at);
      const now = new Date();
      const diff = expiresAt - now;
      
      if (diff <= 0) {
        setTimeRemaining({ expired: true, text: 'Expired', color: 'red' });
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      let color = 'green';
      if (diff < 4 * 60 * 60 * 1000) color = 'red';
      else if (diff < 12 * 60 * 60 * 1000) color = 'yellow';
      
      const text = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
      setTimeRemaining({ expired: false, text, color, hours, minutes });
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, [whatsappWindow]);
  
  if (!whatsappWindow || !timeRemaining) return null;
  
  const colorClasses = {
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30'
  };
  
  if (compact) {
    return (
      <div 
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs border ${colorClasses[timeRemaining.color]}`}
        title={timeRemaining.expired ? 'WhatsApp 24h window expired - Use template messages' : `WhatsApp window closes in ${timeRemaining.text}`}
      >
        <Timer className="h-3 w-3" />
        <span>{timeRemaining.text}</span>
      </div>
    );
  }
  
  return (
    <div 
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border ${colorClasses[timeRemaining.color]}`}
      title={timeRemaining.expired ? 'WhatsApp 24h window expired - Use template messages' : `WhatsApp window closes in ${timeRemaining.text}`}
    >
      <Timer className="h-3.5 w-3.5" />
      <span>{timeRemaining.expired ? 'Window Expired' : timeRemaining.text}</span>
    </div>
  );
};

// WhatsApp Window Expired Banner Component
const WhatsAppWindowExpiredBanner = ({ whatsappWindow, onSendTemplate }) => {
  if (!whatsappWindow || whatsappWindow.is_window_open) return null;
  
  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mx-4 mb-3 flex items-start gap-3">
      <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-red-400">
          24-Hour WhatsApp Window Expired
        </p>
        <p className="text-xs text-wt-text-muted mt-0.5">
          You can only send pre-approved template messages. Regular messages may fail to deliver.
        </p>
      </div>
      {onSendTemplate && (
        <Button 
          size="sm" 
          variant="outline" 
          className="border-red-500/50 text-red-400 hover:bg-red-500/20 flex-shrink-0"
          onClick={onSendTemplate}
        >
          Send Template
        </Button>
      )}
    </div>
  );
};

// Chat List Item Component
const ChatListItem = ({ conversation, isSelected, onClick, onToggleStar, onMarkUnread }) => {
  const initials = conversation.customer_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) { // Less than 24 hours
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) { // Less than 7 days
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };
  
  return (
    <div
      onClick={onClick}
      className={`flex items-start gap-3 p-3 cursor-pointer border-b border-wt-border transition-colors ${
        isSelected ? 'bg-wt-gold/10 border-l-2 border-l-wt-gold' : 'hover:bg-wt-paper'
      }`}
    >
      <div className="relative">
        <Avatar className="h-10 w-10 bg-wt-paper">
          <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-sm">
            {initials}
          </AvatarFallback>
        </Avatar>
        {conversation.channel === 'whatsapp' && (
          <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5">
            <MessageCircle className="h-2.5 w-2.5 text-white" />
          </div>
        )}
        {conversation.channel === 'email' && (
          <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-0.5">
            <Mail className="h-2.5 w-2.5 text-white" />
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            {(() => {
              const flag = flagForRecord(conversation, 'customer_phone_country_code', 'customer_phone');
              return flag ? (
                <span
                  className="text-sm leading-none flex-shrink-0"
                  title={conversation.customer_phone_country_code ? `+${conversation.customer_phone_country_code}` : ''}
                  data-testid="inbox-list-phone-flag"
                >
                  {flag}
                </span>
              ) : null;
            })()}
            <span className="font-medium text-wt-text-primary truncate">
              {conversation.customer_name}
            </span>
          </div>
          <span className="text-xs text-wt-text-muted whitespace-nowrap flex items-center gap-1">
            {formatTime(conversation.last_message_at)}
            {(onToggleStar || onMarkUnread) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-0.5 rounded hover:bg-wt-bg text-wt-text-muted" onClick={(e) => e.stopPropagation()}
                    data-testid={`conv-menu-${conversation.id}`}>
                    <MoreVertical className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-wt-surface border-wt-border" align="end" onClick={(e) => e.stopPropagation()}>
                  {onMarkUnread && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMarkUnread(conversation); }} data-testid={`mark-unread-${conversation.id}`}>
                      Mark as unread
                    </DropdownMenuItem>
                  )}
                  {onToggleStar && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onToggleStar(conversation); }} data-testid={`mark-important-${conversation.id}`}>
                      {conversation.is_starred ? 'Remove important' : 'Mark as important'}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </span>
        </div>
        
        <p className="text-sm text-wt-text-muted truncate mt-0.5">
          {conversation.channel === 'email' && conversation.email_subject ? (
            <span><span className="font-medium text-wt-text-primary/70">{conversation.email_subject}</span> — {conversation.last_message || ''}</span>
          ) : (
            conversation.last_message || 'No messages yet'
          )}
        </p>
        
        <div className="flex items-center gap-2 mt-1">
          {conversation.assigned_to_name && (
            <Badge variant="outline" className="text-xs py-0 h-5 border-wt-border text-wt-text-muted">
              {conversation.assigned_to_name}
            </Badge>
          )}
          {conversation.unread_count > 0 && (
            <Badge className="bg-wt-gold text-black text-xs px-1.5 h-5">
              {conversation.unread_count}
            </Badge>
          )}
          {conversation.is_pinned && (
            <Pin className="h-3 w-3 text-wt-gold" />
          )}
          {conversation.is_starred && (
            <Star className="h-3 w-3 text-wt-gold fill-wt-gold" data-testid="conversation-star-icon" />
          )}
        </div>
      </div>
    </div>
  );
};

// Message Bubble Component
const MessageBubble = ({ message, isOutbound, onRetryMedia, onReply, onReact, showReactionPicker, setShowReactionPicker, reactionEmojis, conversationId }) => {
  const [retrying, setRetrying] = useState(false);
  // Long-press → action sheet state (mobile parity with WhatsApp-native UX)
  const [showActionSheet, setShowActionSheet] = useState(false);
  // Save-as-FAQ chip state — only on outbound HUMAN text replies.
  const [savingFaq, setSavingFaq] = useState(false);
  const [savedFaq, setSavedFaq] = useState(false);
  const pressTimerRef = useRef(null);
  const handleTouchStart = () => {
    pressTimerRef.current = setTimeout(() => {
      setShowActionSheet(true);
    }, 450);
  };
  const handleTouchEnd = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };
  
  const handleRetryMedia = async () => {
    if (retrying || !onRetryMedia) return;
    setRetrying(true);
    try {
      await onRetryMedia(message.id);
    } finally {
      setRetrying(false);
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <Check className="h-3 w-3 text-gray-400" />;
      case 'delivered':
        return <CheckCheck className="h-3 w-3 text-gray-400" />;
      case 'read':
        return <CheckCheck className="h-3 w-3 text-blue-400" />;
      case 'failed':
        return <X className="h-3 w-3 text-red-400" />;
      default:
        return <Clock className="h-3 w-3 text-gray-400" />;
    }
  };
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };
  
  // Long-press handler — see state + handlers declared above.

  return (
    <div
      id={`message-${message.id}`}
      className={`group relative flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-3 transition-colors duration-500 px-2 sm:px-12`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchEnd}
      onContextMenu={(e) => { e.preventDefault(); setShowActionSheet(true); }}
    >
      <div
        className={`relative max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-2 break-words overflow-hidden ${
          isOutbound
            ? 'bg-wt-gold text-black rounded-br-sm'
            : 'bg-wt-surface border border-wt-border text-wt-text-primary rounded-bl-sm'
        }`}
      >
        {/* WhatsApp-style action sheet — opens on long-press (mobile) or
            right-click (desktop). Backdrop click closes it. */}
        {showActionSheet && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={(e) => { e.stopPropagation(); setShowActionSheet(false); }}
          >
            <div
              className="w-full sm:w-80 bg-wt-paper border border-wt-border rounded-t-2xl sm:rounded-2xl p-2 mb-0 sm:mb-auto animate-in slide-in-from-bottom"
              onClick={(e) => e.stopPropagation()}
              data-testid="message-action-sheet"
            >
              <button
                onClick={() => { onReply && onReply(message); setShowActionSheet(false); }}
                className="w-full flex items-center gap-3 p-3 hover:bg-wt-subtle rounded-lg text-left text-wt-text-primary"
                data-testid="action-reply"
              >
                <Reply className="h-5 w-5 text-wt-gold" />
                <span>Reply</span>
              </button>
              <button
                onClick={() => { onReact && onReact(message.id, '👍'); setShowActionSheet(false); }}
                className="w-full flex items-center gap-3 p-3 hover:bg-wt-subtle rounded-lg text-left text-wt-text-primary"
                data-testid="action-react"
              >
                <Smile className="h-5 w-5 text-wt-gold" />
                <span>React</span>
              </button>
              <button
                onClick={() => {
                  if (message.content) {
                    navigator.clipboard?.writeText(message.content);
                    toast.success('Message copied');
                  }
                  setShowActionSheet(false);
                }}
                className="w-full flex items-center gap-3 p-3 hover:bg-wt-subtle rounded-lg text-left text-wt-text-primary"
                data-testid="action-copy"
              >
                <Copy className="h-5 w-5 text-wt-gold" />
                <span>Copy</span>
              </button>
              <button
                onClick={() => setShowActionSheet(false)}
                className="w-full p-3 text-wt-text-muted text-sm mt-1 border-t border-wt-border"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Action buttons — absolutely positioned outside the bubble on desktop.
            Hidden on mobile; replaced by the long-press action sheet above. */}
        <div className={`hidden sm:flex absolute ${isOutbound ? '-left-16 bottom-1' : '-right-16 bottom-1'} items-center gap-1 z-20`}>
          {/* Save as FAQ — outbound HUMAN replies only; teaches the AI. */}
          {isOutbound
            && conversationId
            && message.message_type === 'text'
            && !message.auto_sent
            && !message.ai_fallback
            && (message.content || '').trim().length >= 12
            && (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (savedFaq || savingFaq) return;
                  setSavingFaq(true);
                  try {
                    const token = localStorage.getItem('token');
                    const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
                    await axios.post(
                      `${API_BASE}/ai-copilot/kb/learning/promote-message`,
                      {
                        conversation_id: conversationId,
                        human_reply_message_id: message.id,
                        human_reply_text: message.content,
                      },
                      { headers: { Authorization: `Bearer ${token}` } },
                    );
                    setSavedFaq(true);
                    toast.success('Saved as FAQ — AI will use this next time.');
                  } catch (err) {
                    const msg = err?.response?.data?.detail || 'Could not save as FAQ';
                    toast.error(msg);
                  } finally {
                    setSavingFaq(false);
                  }
                }}
                disabled={savingFaq || savedFaq}
                className={`p-2 rounded-full border transition-colors shadow-md ${
                  savedFaq
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-wt-surface border-wt-border hover:bg-wt-paper hover:border-wt-gold text-wt-text-secondary'
                }`}
                title={savedFaq ? 'Saved to AI knowledge' : 'Save as FAQ — teach the AI to handle this question next time'}
                data-testid={`message-save-faq-btn-${message.id}`}
              >
                {savingFaq ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : savedFaq ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </button>
            )
          }
          {/* Reply button */}
          {onReply && (
            <button
              onClick={() => onReply(message)}
              className="p-2 rounded-full bg-wt-surface border border-wt-border hover:bg-wt-paper hover:border-wt-gold transition-colors shadow-md"
              title="Reply to this message"
              data-testid="message-reply-btn"
            >
              <Reply className="h-4 w-4 text-wt-text-secondary" />
            </button>
          )}
          {/* Reaction button */}
          {onReact && (
            <button
              onClick={() => setShowReactionPicker(showReactionPicker === message.id ? null : message.id)}
              className={`p-2 rounded-full border transition-colors shadow-md ${showReactionPicker === message.id ? 'bg-wt-gold border-wt-gold text-black' : 'bg-wt-surface border-wt-border hover:bg-wt-paper hover:border-wt-gold text-wt-text-secondary'}`}
              title="Add reaction"
              data-testid="message-react-btn"
            >
              <Smile className="h-4 w-4" />
            </button>
          )}
        </div>
        
        {/* Reaction picker */}
        {showReactionPicker === message.id && (
          <div className={`absolute ${isOutbound ? '-left-2 -top-14' : '-right-2 -top-14'} bg-wt-surface border border-wt-border rounded-full px-3 py-2 flex gap-1 shadow-lg z-30`}>
            {reactionEmojis?.map(emoji => (
              <button
                key={emoji}
                onClick={() => {
                  onReact(message.id, emoji);
                  setShowReactionPicker(null);
                }}
                className="hover:scale-125 transition-transform text-xl px-1 py-0.5 rounded hover:bg-wt-paper"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
        {/* Reply Preview - WhatsApp style with thumbnail on right */}
        {message.reply_to && (
          <div 
            className={`mb-2 rounded-lg overflow-hidden flex ${
              isOutbound 
                ? 'bg-black/10' 
                : 'bg-wt-paper'
            }`}
          >
            {/* Left colored border */}
            <div className={`w-1 flex-shrink-0 ${
              message.reply_to.direction === 'inbound' 
                ? 'bg-purple-500' 
                : 'bg-emerald-500'
            }`} />
            
            {/* Content */}
            <div className="flex-1 px-2 py-1.5 min-w-0">
              <p className={`text-[11px] font-semibold ${
                message.reply_to.direction === 'inbound' 
                  ? 'text-purple-400' 
                  : 'text-emerald-400'
              }`}>
                {message.reply_to.direction === 'inbound' ? 'Customer' : 'You'}
              </p>
              <p className={`text-xs truncate ${
                isOutbound ? 'text-black/70' : 'text-wt-text-muted'
              }`}>
                {message.reply_to.has_media && message.reply_to.message_type === 'image' && '📷 Photo'}
                {message.reply_to.has_media && message.reply_to.message_type === 'video' && '🎥 Video'}
                {message.reply_to.has_media && message.reply_to.message_type === 'audio' && '🎵 Audio'}
                {message.reply_to.has_media && message.reply_to.message_type === 'document' && '📄 Document'}
                {!message.reply_to.has_media && (message.reply_to.content || '[Message]')}
              </p>
            </div>
            
            {/* Thumbnail for media messages */}
            {message.reply_to.has_media && message.reply_to.media_url && (
              <div className="w-12 h-12 flex-shrink-0 bg-black/20">
                {message.reply_to.message_type === 'image' && (
                  <img 
                    src={getMediaUrl(message.reply_to.media_url)} 
                    alt="" 
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                {message.reply_to.message_type === 'video' && (
                  <div className="w-full h-full flex items-center justify-center bg-black/40">
                    <Video className="h-5 w-5 text-white/70" />
                  </div>
                )}
                {message.reply_to.message_type === 'document' && (
                  <div className="w-full h-full flex items-center justify-center bg-black/40">
                    <FileText className="h-5 w-5 text-white/70" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Email metadata */}
        {message.email_metadata && (
          <div className={`mb-2 ${isOutbound ? 'text-black/60' : 'text-wt-text-muted'}`}>
            {message.email_metadata.subject && (
              <div className="font-semibold text-sm mb-1 pb-1 border-b border-current/10">{message.email_metadata.subject}</div>
            )}
            <div className="text-xs space-y-0.5">
              {message.email_metadata.from_email && !isOutbound && (
                <div><span className="opacity-60">From:</span> {message.email_metadata.from_name || message.email_metadata.from_email}</div>
              )}
              {message.email_metadata.to && isOutbound && (
                <div><span className="opacity-60">To:</span> {Array.isArray(message.email_metadata.to) ? message.email_metadata.to.join(', ') : message.email_metadata.to}</div>
              )}
              {message.email_metadata.cc && (
                <div><span className="opacity-60">Cc:</span> {message.email_metadata.cc}</div>
              )}
              {message.email_metadata.attachments?.length > 0 && (() => {
                const atts = message.email_metadata.attachments;
                const bodyHtml = message.email_metadata.body_html || '';
                const isImage = (a) => (a.content_type || '').startsWith('image/');
                // Inline cid images already render inside the email body —
                // don't duplicate them in the attachment strip.
                const isInline = (a) => a.content_id && bodyHtml.includes(a.content_id);
                const imageAtts = atts.map((a, i) => [a, i]).filter(([a]) => isImage(a) && !isInline(a));
                const fileAtts = atts.map((a, i) => [a, i]).filter(([a]) => !isImage(a));
                if (!imageAtts.length && !fileAtts.length) return null;
                return (
                  <div className="mt-1.5 space-y-1.5">
                    {imageAtts.length > 0 && (
                      <div className="flex gap-1.5 flex-wrap">
                        {imageAtts.map(([att, i]) => (
                          <EmailAttachmentThumb key={i} messageId={message.id} index={i} att={att} />
                        ))}
                      </div>
                    )}
                    {fileAtts.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="opacity-60">Attachments:</span>
                        {fileAtts.map(([att, i]) => (
                          <button
                            key={i}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openEmailAttachment(message.id, i, att); }}
                            className="bg-black/10 hover:bg-black/25 px-1.5 py-0.5 rounded text-xs underline decoration-dotted underline-offset-2 cursor-pointer transition-colors text-left"
                            title={`Open ${att.filename}`}
                            data-testid={`email-attachment-${message.id}-${i}`}
                          >
                            📎 {att.filename}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Email HTML body rendering */}
        {message.email_metadata?.body_html && (
          <EmailBody body={message.email_metadata.body_html} isOutbound={isOutbound} message={message} />
        )}

        {/* Image */}
        {message.message_type === 'image' && message.media_url && !message.media_url.startsWith('whatsapp_media:') && (
          <>
            <img 
              src={getMediaUrl(message.media_url)}
              alt="Shared" 
              className="max-w-full max-h-64 rounded-lg mb-2 cursor-pointer"
              onClick={() => window.open(getMediaUrl(message.media_url), '_blank')}
              onError={(e) => {
                e.target.style.display = 'none';
                // Show the fallback div
                const fallback = e.target.nextElementSibling;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            <div className="items-center gap-2 p-3 bg-black/10 rounded-lg mb-2" style={{ display: 'none' }}>
              <ImageIcon className="h-8 w-8 text-gray-400" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Image failed to load</p>
                <p className="text-[10px] text-gray-400">File may be unavailable</p>
              </div>
            </div>
          </>
        )}
        {/* Image unavailable - show placeholder with retry option */}
        {message.message_type === 'image' && (!message.media_url || message.media_url.startsWith('whatsapp_media:')) && (
          <div className="flex items-center gap-2 p-3 bg-black/10 rounded-lg mb-2">
            <ImageIcon className="h-8 w-8 text-gray-400" />
            <div className="flex-1">
              <p className="text-xs text-gray-500">Image not available</p>
              <p className="text-[10px] text-gray-400">Media may have expired or still downloading</p>
            </div>
            {message.media_url?.startsWith('whatsapp_media:') && onRetryMedia && (
              <button
                onClick={handleRetryMedia}
                disabled={retrying}
                className="text-xs px-2 py-1 bg-wt-gold/20 hover:bg-wt-gold/30 text-wt-gold rounded transition-colors disabled:opacity-50"
              >
                {retrying ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              </button>
            )}
          </div>
        )}
        
        {/* Video */}
        {message.message_type === 'video' && message.media_url && !message.media_url.startsWith('whatsapp_media:') && (
          <>
            <video 
              src={getMediaUrl(message.media_url)}
              controls
              className="max-w-full max-h-64 rounded-lg mb-2"
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = e.target.nextElementSibling;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            <div className="items-center gap-2 p-3 bg-black/10 rounded-lg mb-2" style={{ display: 'none' }}>
              <Video className="h-8 w-8 text-gray-400" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Video failed to load</p>
                <p className="text-[10px] text-gray-400">File may be unavailable</p>
              </div>
            </div>
          </>
        )}
        {/* Video unavailable - show placeholder with retry */}
        {message.message_type === 'video' && (!message.media_url || message.media_url.startsWith('whatsapp_media:')) && (
          <div className="flex items-center gap-2 p-3 bg-black/10 rounded-lg mb-2">
            <Video className="h-8 w-8 text-gray-400" />
            <div className="flex-1">
              <p className="text-xs text-gray-500">Video not available</p>
              <p className="text-[10px] text-gray-400">Media may have expired or still downloading</p>
            </div>
            {message.media_url?.startsWith('whatsapp_media:') && onRetryMedia && (
              <button
                onClick={handleRetryMedia}
                disabled={retrying}
                className="text-xs px-2 py-1 bg-wt-gold/20 hover:bg-wt-gold/30 text-wt-gold rounded transition-colors disabled:opacity-50"
              >
                {retrying ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              </button>
            )}
          </div>
        )}
        
        {/* Audio - WhatsApp Style Player */}
        {message.message_type === 'audio' && message.media_url && !message.media_url.startsWith('whatsapp_media:') && (
          <div className="mb-2">
            <AudioPlayer src={getMediaUrl(message.media_url)} isOutbound={isOutbound} />
          </div>
        )}
        {/* Audio unavailable - show placeholder with retry */}
        {message.message_type === 'audio' && (!message.media_url || message.media_url.startsWith('whatsapp_media:')) && (
          <div className="flex items-center gap-3 min-w-[200px] mb-2">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isOutbound ? 'bg-black/20' : 'bg-wt-paper'}`}>
              <Mic className="h-6 w-6 text-gray-400" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500">Voice message</p>
              <p className="text-[10px] text-gray-400">Loading or unavailable</p>
            </div>
            {message.media_url?.startsWith('whatsapp_media:') && onRetryMedia && (
              <button
                onClick={handleRetryMedia}
                disabled={retrying}
                className="text-xs px-2 py-1 bg-wt-gold/20 hover:bg-wt-gold/30 text-wt-gold rounded transition-colors disabled:opacity-50"
              >
                {retrying ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              </button>
            )}
          </div>
        )}
        
        {/* Document */}
        {message.message_type === 'document' && message.media_url && !message.media_url.startsWith('whatsapp_media:') && (
          <a 
            href={getMediaUrl(message.media_url)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-2 bg-black/10 rounded-lg mb-2 hover:bg-black/20"
          >
            <FileText className="h-5 w-5" />
            <span className="text-sm underline">Download Document</span>
          </a>
        )}
        
        {/* Message content - hide [Image]/[Video]/[Audio]/[Document] text if media is shown.
            Also hide for emails already rendered via body_html above — rendering
            both duplicated every email's text inside one bubble. */}
        {!message.email_metadata?.body_html &&
         !(message.media_url && !message.media_url.startsWith('whatsapp_media:') && 
           ['image', 'video', 'audio', 'document'].includes(message.message_type)) && (
          <p className="text-sm whitespace-pre-wrap">
            {message.content === '[Image]' ? 'Shared a photo' : 
             message.content === '[Video]' ? 'Shared a video' :
             message.content === '[Audio message]' ? 'Sent a voice message' :
             renderTextWithLinks(message.content)}
          </p>
        )}
        {/* Show caption for media messages that have valid media_url and non-placeholder content */}
        {message.media_url && !message.media_url.startsWith('whatsapp_media:') && 
         ['image', 'video', 'document'].includes(message.message_type) && 
         message.content && 
         !['[Image]', '[Video]', '[Document]'].includes(message.content) && (
          <p className="text-sm whitespace-pre-wrap break-words" style={{ overflowWrap: 'anywhere' }}>{renderTextWithLinks(message.content)}</p>
        )}

        {/* Template buttons — QUICK_REPLY / URL / PHONE_NUMBER.
            Rendered read-only since the merchant cannot tap their own
            button; it reflects what the customer actually sees in
            WhatsApp so the agent has full context. */}
        {Array.isArray(message.template_buttons) && message.template_buttons.length > 0 && (
          <div
            className={`mt-2 -mx-3 -mb-2 border-t ${isOutbound ? 'border-black/10' : 'border-wt-border'}`}
            data-testid="inbox-msg-template-buttons"
          >
            {message.template_buttons.map((btn, idx) => {
              const label = btn?.text || '';
              const key = `${message.id}-btn-${idx}`;
              const base = `block w-full text-center text-sm py-2 px-3 font-medium ${isOutbound ? 'text-blue-700 hover:bg-black/5' : 'text-blue-400 hover:bg-wt-paper'} ${idx > 0 ? (isOutbound ? 'border-t border-black/10' : 'border-t border-wt-border') : ''}`;
              if (btn?.type === 'URL' && btn?.url) {
                return (
                  <a
                    key={key}
                    href={btn.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={base}
                    data-testid={`inbox-msg-template-btn-${idx}`}
                  >
                    {label || 'Open link'}
                  </a>
                );
              }
              if (btn?.type === 'PHONE_NUMBER' && btn?.phone_number) {
                return (
                  <a
                    key={key}
                    href={`tel:${btn.phone_number}`}
                    className={base}
                    data-testid={`inbox-msg-template-btn-${idx}`}
                  >
                    {label || btn.phone_number}
                  </a>
                );
              }
              // QUICK_REPLY — read-only preview for the agent
              return (
                <div
                  key={key}
                  className={base + ' cursor-default select-none'}
                  data-testid={`inbox-msg-template-btn-${idx}`}
                  title="Quick reply — customers tap this on their device"
                >
                  {label || 'Quick reply'}
                </div>
              );
            })}
          </div>
        )}
        
        <div className={`flex items-center justify-end gap-1 mt-1 ${
          isOutbound ? 'text-black/60' : 'text-wt-text-muted'
        }`}>
          {/* Show a small automated-source pill on journey / campaign / bulk
             marketing outbound messages so agents immediately know the
             message was sent by an automated flow, not a teammate. */}
          {isOutbound && message.is_automated && message.source && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-black/15 mr-1"
              data-testid="inbox-msg-automated-source"
              title={message.sender_name || message.source}
            >
              ⚙ {message.sender_name || message.source.replace('_', ' ')}
            </span>
          )}
          {/* Show sender name for outbound messages */}
          {isOutbound && !message.is_automated && message.sender_name && (
            <span className="text-xs font-medium mr-1">{message.sender_name}</span>
          )}
          <span className="text-xs">{formatTime(message.timestamp)}</span>
          {isOutbound && getStatusIcon(message.status)}
        </div>
        
        {/* Reaction display */}
        {message.reaction && (
          <div className={`absolute -bottom-2 ${isOutbound ? 'left-2' : 'right-2'} bg-wt-surface border border-wt-border rounded-full px-1.5 py-0.5 text-sm shadow-sm`}>
            {message.reaction}
          </div>
        )}
      </div>
    </div>
  );
};

// Customer Info Panel Component
const CustomerInfoPanel = ({ conversation, customer, orders, onClose, formatCurrency }) => {
  if (!conversation) return null;
  
  return (
    <div className="w-80 border-l border-wt-border bg-wt-surface flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-wt-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-wt-text-primary">Customer Info</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="lg:hidden">
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12">
            <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
              {conversation.customer_name?.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            {customer?.id ? (
              <Link
                to={`/customers?customer_id=${customer.id}`}
                className="font-medium text-wt-text-primary hover:text-wt-gold hover:underline truncate block"
                data-testid="inbox-customer-profile-link"
                title="Open customer profile"
              >
                {conversation.customer_name}
              </Link>
            ) : (
              <p className="font-medium text-wt-text-primary truncate">{conversation.customer_name}</p>
            )}
            <p className="text-sm text-wt-text-muted truncate" data-testid="inbox-detail-phone-summary">
              {(() => {
                const flag = flagForRecord(conversation, 'customer_phone_country_code', 'customer_phone');
                return flag ? (
                  <span className="mr-1" title={conversation.customer_phone_country_code ? `+${conversation.customer_phone_country_code}` : ''}>
                    {flag}
                  </span>
                ) : null;
              })()}
              {conversation.customer_phone}
            </p>
          </div>
        </div>
      </div>
      
      {/* Contact Info */}
      <div className="p-4 border-b border-wt-border space-y-3">
        {conversation.customer_phone && (
          <div className="flex items-center gap-3 text-sm">
            <Phone className="h-4 w-4 text-wt-text-muted" />
            {(() => {
              const flag = flagForRecord(conversation, 'customer_phone_country_code', 'customer_phone');
              return flag ? (
                <span
                  className="text-base leading-none"
                  title={conversation.customer_phone_country_code ? `+${conversation.customer_phone_country_code}` : ''}
                  data-testid="inbox-contact-phone-flag"
                >
                  {flag}
                </span>
              ) : null;
            })()}
            <span className="text-wt-text-primary" data-testid="inbox-contact-phone">{conversation.customer_phone}</span>
          </div>
        )}
        {conversation.customer_email && (
          <div className="flex items-center gap-3 text-sm">
            <Mail className="h-4 w-4 text-wt-text-muted" />
            <span className="text-wt-text-primary">{conversation.customer_email}</span>
          </div>
        )}
        {customer && (
          <>
            <div className="flex items-center gap-3 text-sm">
              <ShoppingBag className="h-4 w-4 text-wt-text-muted" />
              <span className="text-wt-text-primary">{customer.total_orders || 0} Orders</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Star className="h-4 w-4 text-wt-text-muted" />
              <span className="text-wt-text-primary">{formatCurrency(customer.total_spent || 0)}</span>
            </div>
          </>
        )}
      </div>
      
      {/* Recent Orders */}
      <div className="flex-1 overflow-y-auto p-4">
        <h4 className="text-sm font-medium text-wt-text-primary mb-3 flex items-center gap-2">
          <Package className="h-4 w-4" />
          Recent Orders
        </h4>
        
        {orders && orders.length > 0 ? (
          <div className="space-y-3">
            {orders.map((order) => (
              <div
                key={order.id}
                className="p-3 bg-wt-paper rounded-lg border border-wt-border"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-wt-text-primary">
                    #{order.order_number}
                  </span>
                  <Badge className={`text-xs ${
                    order.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                    order.status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {order.status}
                  </Badge>
                </div>
                <p className="text-xs text-wt-text-muted">
                  {new Date(order.created_at).toLocaleDateString()}
                </p>
                <p className="text-sm font-medium text-wt-gold mt-1">
                  {formatCurrency(order.total_amount)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-wt-text-muted text-center py-4">No orders found</p>
        )}
      </div>
    </div>
  );
};

// Main Inbox Page Component
const InboxPage = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useSettings();
  const [currentUser, setCurrentUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [starredFilter, setStarredFilter] = useState(false);
  const [assignmentFilter, setAssignmentFilter] = useState('all'); // 'all', 'mine', 'unassigned'
  const [channelFilter, setChannelFilter] = useState('whatsapp'); // 'whatsapp' or 'instagram'
  const [instagramConversations, setInstagramConversations] = useState([]);
  const [loadingInstagram, setLoadingInstagram] = useState(false);
  const [showCustomerPanel, setShowCustomerPanel] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [stats, setStats] = useState({ open: 0, pending: 0, unassigned: 0 });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [showCannedResponses, setShowCannedResponses] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParams, setTemplateParams] = useState([]);
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [pastedImage, setPastedImage] = useState(null);
  const [pastedImagePreview, setPastedImagePreview] = useState(null);
  
  // Pagination state
  const [conversationPage, setConversationPage] = useState(1);
  const [conversationTotal, setConversationTotal] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  // Suppresses the "auto-scroll to bottom on every messages change"
  // effect ONLY for the single render that comes from
  // `loadMoreMessages`. We need a ref (not state) so it can be
  // cleared synchronously inside the same effect that uses it,
  // without triggering another re-render.
  const isPrependingRef = useRef(false);
  // Scroll-anchor snapshot taken right before prepending older
  // messages. `(scrollHeight - scrollTop)` is the distance from the
  // bottom of the message list; after the prepend we restore it so
  // the user stays exactly on the message they were reading.
  const scrollAnchorRef = useRef(null);
  const [messagesTotal, setMessagesTotal] = useState(0);
  
  // Sound settings
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('inboxSoundEnabled');
    return saved === null ? true : saved === 'true';
  });
  
  // New Features State
  const [replyToMessage, setReplyToMessage] = useState(null); // Quote reply
  const [messageSearch, setMessageSearch] = useState(''); // Search within conversation
  const [showMessageSearch, setShowMessageSearch] = useState(false);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [isRecording, setIsRecording] = useState(false); // Voice recording
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [showReactionPicker, setShowReactionPicker] = useState(null); // Message ID for reaction picker
  const [isTyping, setIsTyping] = useState(false); // Typing indicator
  const [recordingUsers, setRecordingUsers] = useState({}); // Users recording voice messages
  const typingTimeoutRef = useRef(null);
  
  // Email compose state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [selectedEmailAccount, setSelectedEmailAccount] = useState(null);
  const [composeDialogOpen, setComposeDialogOpen] = useState(false);
  const [composeData, setComposeData] = useState({ to: '', subject: '', body: '', cc: '' });
  const [composeSending, setComposeSending] = useState(false);
  
  // Reaction emojis
  const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
  
  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);

  // Auto-grow textarea — keeps it in sync with `newMessage` state so external
  // updates (emoji-picker insert, canned-response paste, send-clearing etc.)
  // also recompute the height. Runs after every newMessage change.
  useEffect(() => {
    const ta = messageInputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    if (newMessage) {
      ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
    }
  }, [newMessage]);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const notificationSoundRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  // Audio context reference for sound notifications
  const audioContextRef = useRef(null);
  
  // WhatsApp-like notification sound using Web Audio API
  useEffect(() => {
    // Create a WhatsApp-like notification sound function
    const createNotificationSound = () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        
        // Create a short "ding dong" sound like WhatsApp
        const playSound = async () => {
          // Resume audio context if suspended (browser autoplay policy)
          if (audioContext.state === 'suspended') {
            await audioContext.resume();
          }
          
          const now = audioContext.currentTime;
          
          // First tone (higher pitch)
          const osc1 = audioContext.createOscillator();
          const gain1 = audioContext.createGain();
          osc1.connect(gain1);
          gain1.connect(audioContext.destination);
          osc1.type = 'sine';
          osc1.frequency.setValueAtTime(1318.5, now); // E6 note
          gain1.gain.setValueAtTime(0.3, now);
          gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
          osc1.start(now);
          osc1.stop(now + 0.15);
          
          // Second tone (lower pitch) - delayed slightly
          const osc2 = audioContext.createOscillator();
          const gain2 = audioContext.createGain();
          osc2.connect(gain2);
          gain2.connect(audioContext.destination);
          osc2.type = 'sine';
          osc2.frequency.setValueAtTime(987.8, now + 0.08); // B5 note
          gain2.gain.setValueAtTime(0, now);
          gain2.gain.setValueAtTime(0.25, now + 0.08);
          gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
          osc2.start(now + 0.08);
          osc2.stop(now + 0.25);
        };
        
        return { play: playSound, context: audioContext };
      } catch (e) {
        console.log('Web Audio API not supported');
        return { play: () => {}, context: null };
      }
    };
    
    notificationSoundRef.current = createNotificationSound();
    
    // Cleanup
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);
  
  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && notificationSoundRef.current && notificationSoundRef.current.play) {
      try {
        notificationSoundRef.current.play();
      } catch (e) {
        // Ignore autoplay errors
      }
    }
  }, [soundEnabled]);

  // Browser autoplay-policy unlock.
  //
  // AudioContext starts in `suspended` state and stays suspended until
  // the user interacts with the page. Without this, the FIRST inbound
  // message after a fresh page load is silent — the user just sees the
  // toast and assumes "sound notifications don't work". Resuming on
  // first click/keypress wakes the context so subsequent
  // `playNotificationSound()` calls actually emit audio.
  useEffect(() => {
    const tryResume = async () => {
      try {
        const ctx = audioContextRef.current;
        if (ctx && ctx.state === 'suspended') {
          await ctx.resume();
        }
        // Also fire a silent zero-gain blip so any deferred buffer
        // graph is primed. Cheap; happens once per session.
        if (notificationSoundRef.current && notificationSoundRef.current.context) {
          const c = notificationSoundRef.current.context;
          const osc = c.createOscillator();
          const g = c.createGain();
          g.gain.value = 0;
          osc.connect(g); g.connect(c.destination);
          osc.start(); osc.stop(c.currentTime + 0.01);
        }
      } catch (e) {
        // Best effort — don't crash the page if the context is gone.
      }
      window.removeEventListener('pointerdown', tryResume);
      window.removeEventListener('keydown', tryResume);
    };
    window.addEventListener('pointerdown', tryResume, { once: true });
    window.addEventListener('keydown', tryResume, { once: true });
    return () => {
      window.removeEventListener('pointerdown', tryResume);
      window.removeEventListener('keydown', tryResume);
    };
  }, []);

  // ----- Desktop browser notifications (OS-level toast) -----
  //
  // Agents often have the Opswarex tab in the background while they're
  // in WhatsApp Web, Slack, Gmail, etc. A native OS notification pops
  // even when the tab isn't focused — combined with the sound, the
  // agent will never miss a customer reply.
  //
  // We request permission lazily after the user's FIRST interaction
  // (browsers reject `requestPermission()` calls made before any user
  // gesture). Storing the result in `desktopNotifPermissionRef` lets
  // the message-arrival code paths check synchronously.
  const desktopNotifPermissionRef = useRef(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );

  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'granted' || Notification.permission === 'denied') {
      desktopNotifPermissionRef.current = Notification.permission;
      return;
    }
    // permission === 'default' → ask once, on first user gesture.
    const askOnce = async () => {
      try {
        const res = await Notification.requestPermission();
        desktopNotifPermissionRef.current = res;
      } catch (e) {
        // Some browsers (older Safari) reject promise-based call —
        // fall back to callback signature.
        try {
          Notification.requestPermission((res) => {
            desktopNotifPermissionRef.current = res;
          });
        } catch (_) { /* give up silently */ }
      }
      window.removeEventListener('pointerdown', askOnce);
      window.removeEventListener('keydown', askOnce);
    };
    window.addEventListener('pointerdown', askOnce, { once: true });
    window.addEventListener('keydown', askOnce, { once: true });
    return () => {
      window.removeEventListener('pointerdown', askOnce);
      window.removeEventListener('keydown', askOnce);
    };
  }, []);

  // Fire a desktop notification for an inbound message.
  // Only fires when the user has granted permission AND the tab is
  // backgrounded (`document.hidden`) — surfacing notifications while
  // the agent is already looking at the inbox would just be noise.
  const showDesktopNotification = useCallback((title, body, conversationId) => {
    try {
      if (typeof Notification === 'undefined') return;
      if (desktopNotifPermissionRef.current !== 'granted') return;
      if (!document.hidden) return;
      const n = new Notification(title, {
        body: body || '',
        icon: '/logo192.png',
        // `tag` collapses repeated notifs from the SAME conversation
        // into one entry in the OS notification centre — prevents
        // spam when a customer sends 5 messages in 10 seconds.
        tag: conversationId ? `opswarex-inbox-${conversationId}` : 'opswarex-inbox',
        renotify: true,
      });
      // Focus the tab + jump to the conversation on click.
      n.onclick = () => {
        try {
          window.focus();
          if (conversationId) {
            // Drop a small marker in the URL so deep-link works on reload.
            window.history.replaceState({}, '', `/marketing/inbox?conv=${conversationId}`);
          }
        } catch (_) { /* ignore */ }
        n.close();
      };
      // Auto-close after 8s so the OS centre doesn't pile up forever.
      setTimeout(() => { try { n.close(); } catch (_) {} }, 8000);
    } catch (_) {
      // Notifications can throw on edge cases (permissions revoked
      // mid-session, etc.). Never let it bubble.
    }
  }, []);
  
  // Toggle sound and save preference
  const toggleSound = async () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('inboxSoundEnabled', newValue.toString());
    
    // Resume audio context on user interaction (required by browser autoplay policy)
    if (newValue && audioContextRef.current && audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
      } catch (e) {
        console.log('Failed to resume audio context');
      }
    }
    
    // Play a test sound when enabling
    if (newValue && notificationSoundRef.current?.play) {
      try {
        await notificationSoundRef.current.play();
      } catch (e) {
        // Ignore errors
      }
    }
    
    toast.success(newValue ? 'Sound notifications enabled' : 'Sound notifications disabled');
  };
  
  // Conversations pagination
  const CONVERSATIONS_PER_PAGE = 30;
  
  // Track latest loaded depth in a ref so the polling effect can pass
  // `refresh=true` without re-binding every time conversations grows
  // (which would re-create the interval and skip a tick).
  const loadedConversationCountRef = useRef(0);
  
  // Load conversations
  //
  // Modes:
  //  - page=1, append=false (default): hard reset to the first page.
  //    Used by tab/filter/search changes.
  //  - page=N, append=true: append the Nth page. Used by "Load More".
  //  - refresh=true: re-fetch the ENTIRE currently-loaded window
  //    (skip=0, limit=N already loaded) and REPLACE the list. Used by
  //    the polling effect so the user doesn't lose their loaded
  //    depth every 10s. Without this, clicking "Load More" only
  //    "worked" until the next poll tick truncated the list back to
  //    the first 30 rows and re-showed "Load More (98 remaining)".
  const loadConversations = useCallback(async (page = 1, append = false, refresh = false) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      // Channel filter - pass to API
      if (channelFilter === 'email') {
        params.append('channel', 'email');
      } else if (channelFilter === 'whatsapp') {
        params.append('channel', 'whatsapp');
      }
      
      // When 'all' is selected, exclude closed conversations
      // Only show closed conversations when explicitly filtered
      if (statusFilter === 'all') {
        params.append('exclude_status', 'closed');
      } else if (statusFilter !== 'closed') {
        params.append('status', statusFilter);
      } else {
        // Show closed conversations when 'closed' filter is selected
        params.append('status', 'closed');
      }
      
      // Assignment filter
      if (assignmentFilter === 'mine') {
        params.append('assigned_to', currentUser?.id || '');
      } else if (assignmentFilter === 'unassigned') {
        params.append('unassigned', 'true');
      }
      
      if (starredFilter) params.append('starred', 'true');
      
      if (searchQuery) params.append('search', searchQuery);
      
      // Pagination params — refresh fetches the full loaded window so
      // polling never collapses the user's "Load More" expansion.
      if (refresh) {
        const loaded = Math.max(loadedConversationCountRef.current, CONVERSATIONS_PER_PAGE);
        params.append('limit', loaded.toString());
        params.append('skip', '0');
      } else {
        params.append('limit', CONVERSATIONS_PER_PAGE.toString());
        params.append('skip', ((page - 1) * CONVERSATIONS_PER_PAGE).toString());
      }
      
      const response = await axios.get(`${API_BASE_URL}/inbox/conversations?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newConversations = response.data.conversations || [];
      
      if (append) {
        setConversations(prev => {
          const merged = [...prev, ...newConversations];
          loadedConversationCountRef.current = merged.length;
          return merged;
        });
      } else {
        // refresh OR hard reset — both replace the list. For refresh,
        // newConversations.length equals the prior loaded depth, so
        // the user keeps the same scroll-able list with fresh data.
        setConversations(newConversations);
        loadedConversationCountRef.current = newConversations.length;
      }
      
      setConversationTotal(response.data.total || 0);
      if (!refresh) {
        setConversationPage(page);
      }
    } catch (error) {
      console.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, assignmentFilter, searchQuery, currentUser?.id, channelFilter, starredFilter]);
  
  // Load more conversations
  const loadMoreConversations = () => {
    if (conversations.length < conversationTotal) {
      loadConversations(conversationPage + 1, true);
    }
  };
  
  // Load stats — pass the active channel so per-tab badge counts
  // (Open 87 / Pending 12 / …) match the actual list under the
  // selected channel pill.
  const loadStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (channelFilter === 'whatsapp' || channelFilter === 'email') {
        params.append('channel', channelFilter);
      }
      const qs = params.toString();
      const url = `${API_BASE_URL}/inbox/stats${qs ? '?' + qs : ''}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  }, [channelFilter]);
  
  // Load team members
  const loadTeamMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/inbox/team-members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data.users || []);
    } catch (error) {
      console.error('Failed to load team members');
    }
  };
  
  // Load canned responses
  const loadCannedResponses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/inbox/canned-responses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCannedResponses(response.data.responses || []);
    } catch (error) {
      console.error('Failed to load canned responses');
    }
  };
  
  // Load WhatsApp templates
  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/inbox/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to load templates');
    }
  };
  
  // Load Instagram conversations
  const loadInstagramConversations = async () => {
    setLoadingInstagram(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/instagram/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstagramConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to load Instagram conversations');
    } finally {
      setLoadingInstagram(false);
    }
  };

  // Load email accounts for sending
  const loadEmailAccounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/email/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const accs = res.data.accounts || [];
      setEmailAccounts(accs);
      if (accs.length > 0 && !selectedEmailAccount) {
        setSelectedEmailAccount(accs[0].id);
      }
    } catch (err) {
      console.error('Failed to load email accounts');
    }
  }, [selectedEmailAccount]);

  // Send email message
  const handleSendEmail = async () => {
    if (!selectedConversation || !newMessage.trim()) return;
    if (!selectedEmailAccount) {
      toast.error('No email account connected. Go to Integrations > Email to set one up.');
      return;
    }

    const toAddr = emailTo || selectedConversation.customer_email;
    if (!toAddr) {
      toast.error('No recipient email address');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const subject = emailSubject || selectedConversation.email_subject || 'No Subject';
      const ccList = emailCc ? emailCc.split(',').map(e => e.trim()).filter(Boolean) : [];
      
      // Get the last inbound email message_id for threading
      const lastInbound = [...messages].reverse().find(m => m.direction === 'inbound' && m.email_metadata?.message_id);
      const inReplyTo = lastInbound?.email_metadata?.message_id || null;
      const references = lastInbound?.email_metadata?.references || inReplyTo || null;

      await axios.post(`${API_BASE_URL}/email/send`, {
        account_id: selectedEmailAccount,
        to: [toAddr],
        subject: subject,
        body_html: `<p>${newMessage.replace(/\n/g, '<br>')}</p>`,
        body_text: newMessage,
        cc: ccList.length > 0 ? ccList : null,
        in_reply_to: inReplyTo,
        references: references,
        conversation_id: selectedConversation.id
      }, { headers: { Authorization: `Bearer ${token}` } });

      setNewMessage('');
      setEmailSubject('');
      setEmailCc('');
      toast.success('Email sent');

      // Refresh messages — must pass the conversation id, otherwise
      // refreshMessages bails immediately and the new outbound message
      // doesn't render until a manual page refresh.
      refreshMessages(selectedConversation.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Compose new email (creates conversation + sends)
  const handleComposeNewEmail = async () => {
    if (!composeData.to || !composeData.subject || !composeData.body) {
      toast.error('Please fill in To, Subject, and Body');
      return;
    }
    if (!selectedEmailAccount) {
      toast.error('No email account connected. Go to Integrations > Email.');
      return;
    }
    setComposeSending(true);
    try {
      const token = localStorage.getItem('token');
      const ccList = composeData.cc ? composeData.cc.split(',').map(e => e.trim()).filter(Boolean) : [];
      
      const res = await axios.post(`${API_BASE_URL}/email/compose`, {
        account_id: selectedEmailAccount,
        to: [composeData.to.trim()],
        subject: composeData.subject,
        body_html: `<p>${composeData.body.replace(/\n/g, '<br>')}</p>`,
        body_text: composeData.body,
        cc: ccList.length > 0 ? ccList : null
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Email sent!');
      setComposeDialogOpen(false);
      setComposeData({ to: '', subject: '', body: '', cc: '' });
      loadConversations(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setComposeSending(false);
    }
  };
  
  // Sync Instagram conversations from Meta API
  const syncInstagramConversations = async () => {
    setLoadingInstagram(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/instagram/conversations/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Synced ${response.data.synced_conversations} conversations from Instagram`);
        // Reload conversations after sync
        await loadInstagramConversations();
      } else {
        toast.error('Failed to sync Instagram conversations');
      }
    } catch (error) {
      console.error('Failed to sync Instagram conversations:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync Instagram conversations');
    } finally {
      setLoadingInstagram(false);
    }
  };
  
  // Load Instagram messages for a conversation
  const loadInstagramMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/instagram/conversations/${conversationId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data.messages || []);
      // Update selected conversation with full data
      if (response.data.conversation) {
        setSelectedConversation({
          ...response.data.conversation,
          channel: 'instagram'
        });
      }
    } catch (error) {
      console.error('Failed to load Instagram messages');
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };
  
  // Send Instagram message
  const sendInstagramMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/instagram/conversations/${selectedConversation.id}/send`,
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setMessages(prev => [...prev, response.data.message]);
        setNewMessage('');
        loadInstagramConversations(); // Refresh conversation list
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send message';
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };
  
  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id;
    
    if (!token || !userId) return;
    
    // Determine WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = process.env.REACT_APP_BACKEND_URL.replace(/^https?:/, wsProtocol);
    const wsUrl = `${backendUrl}/ws/inbox/${userId}?token=${token}`;
    
    console.log('[WS] Connecting to:', wsUrl);
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      // Connection timeout - if not connected in 5s, give up
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.log('[WS] Connection timeout, falling back to polling');
          ws.close();
          setWsConnected(false);
        }
      }, 5000);
      
      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('[WS] Connected');
        setWsConnected(true);
        
        // The `watch_conversation` useEffect below handles subscribing to
        // the current conversation whenever selection changes. Do NOT read
        // `selectedConversation` here — doing so would force this callback
        // to depend on it and recreate the WebSocket on every chat switch,
        // which races with loadConversationDetails and hangs the spinner.
        
        // Start ping interval
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
        
        ws.pingInterval = pingInterval;
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'new_message':
              handleNewMessageWS(data);
              break;
            case 'conversation_update':
              handleConversationUpdateWS(data);
              break;
            case 'typing':
              handleTypingIndicatorWS(data);
              break;
            case 'recording':
              handleRecordingIndicatorWS(data);
              break;
            case 'pong':
              // Pong received, connection is alive
              break;
            default:
              console.log('[WS] Unknown message type:', data.type);
          }
        } catch (e) {
          console.error('[WS] Error parsing message:', e);
        }
      };
      
      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log('[WS] Disconnected:', event.code, event.reason);
        setWsConnected(false);
        if (ws.pingInterval) clearInterval(ws.pingInterval);
        
        // Only try to reconnect if this wasn't intentional and we've successfully connected before
        // Don't keep retrying if the environment doesn't support WebSockets
        if (event.code !== 1000 && event.code !== 1006) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[WS] Attempting to reconnect...');
            connectWebSocket();
          }, 5000);
        }
      };
      
      ws.onerror = (error) => {
        console.error('[WS] Error:', error);
        clearTimeout(connectionTimeout);
      };
      
    } catch (error) {
      console.error('[WS] Failed to connect:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Handle new message from WebSocket
  const handleNewMessageWS = useCallback((data) => {
    const { conversation_id, message } = data;
    
    // Update messages if this is the current conversation
    if (selectedConversation && selectedConversation.id === conversation_id) {
      setMessages(prev => {
        // Check if message already exists
        if (prev.some(m => m.id === message.id)) return prev;
        return [...prev, message];
      });
    }
    
    // Update conversation list
    setConversations(prev => {
      const updated = prev.map(conv => {
        if (conv.id === conversation_id) {
          return {
            ...conv,
            last_message: message.content?.substring(0, 100),
            last_message_at: message.timestamp,
            unread_count: (conv.unread_count || 0) + (message.direction === 'inbound' ? 1 : 0)
          };
        }
        return conv;
      });
      // Sort by last_message_at
      return updated.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
    });
    
    // Play notification sound + fire desktop notif for inbound messages.
    if (message.direction === 'inbound') {
      playNotificationSound();
      if (document.hidden) {
        toast.info(`New message from ${message.sender_name || 'Customer'}`);
        showDesktopNotification(
          `New message from ${message.sender_name || 'Customer'}`,
          (message.content || '').slice(0, 140),
          conversation_id,
        );
      }
    }
    
    loadStats();
  }, [selectedConversation, playNotificationSound, showDesktopNotification, loadStats]);
  
  // Handle conversation update from WebSocket
  const handleConversationUpdateWS = (data) => {
    const { conversation } = data;
    setConversations(prev => prev.map(conv => 
      conv.id === conversation.id ? { ...conv, ...conversation } : conv
    ));
    
    if (selectedConversation && selectedConversation.id === conversation.id) {
      setSelectedConversation(prev => ({ ...prev, ...conversation }));
    }
  };
  
  // Handle typing indicator from WebSocket
  const handleTypingIndicatorWS = (data) => {
    const { conversation_id, user_id, user_name, is_typing } = data;
    
    setTypingUsers(prev => {
      const convTyping = prev[conversation_id] || {};
      if (is_typing) {
        return { ...prev, [conversation_id]: { ...convTyping, [user_id]: user_name } };
      } else {
        const { [user_id]: removed, ...rest } = convTyping;
        return { ...prev, [conversation_id]: rest };
      }
    });
  };
  
  // Handle recording indicator from WebSocket
  const handleRecordingIndicatorWS = (data) => {
    const { conversation_id, user_id, user_name, is_recording } = data;
    
    setRecordingUsers(prev => {
      const convRecording = prev[conversation_id] || {};
      if (is_recording) {
        return { ...prev, [conversation_id]: { ...convRecording, [user_id]: user_name } };
      } else {
        const { [user_id]: removed, ...rest } = convRecording;
        return { ...prev, [conversation_id]: rest };
      }
    });
  };
  
  // Send typing indicator
  const sendTypingIndicator = (isTyping) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && selectedConversation) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        conversation_id: selectedConversation.id,
        is_typing: isTyping
      }));
    }
  };
  
  // Load conversation details
  const loadConversationDetails = async (conversationId) => {
    // Guard: if already on this conversation, just reopen the mobile pane
    // (avoids the 2nd-chat blank screen caused by mid-flight state churn
    // when selectedConversation / messages were being replaced in-flight).
    if (selectedConversation?.id === conversationId && messages.length > 0) {
      if (isMobile) setShowMobileChat(true);
      return;
    }
    // Reset previous chat state IMMEDIATELY so nothing stale renders during
    // the async fetch. This also prevents React from matching old
    // MessageBubble keys across conversations.
    setMessages([]);
    setSelectedConversation(null);
    setCustomer(null);
    setOrders([]);
    setShowReactionPicker(null);
    setReplyToMessage(null);
    setLoadingMessages(true);
    setHasMoreMessages(false);
    // Flip to the mobile chat pane up-front so the user sees the loader
    // immediately (rather than the list continuing to show while the chat
    // loads in the background).
    if (isMobile) setShowMobileChat(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/inbox/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedConversation(response.data.conversation);
      setMessages(response.data.messages || []);
      setCustomer(response.data.customer_info);
      setOrders(response.data.recent_orders || []);
      
      // Check if there are more messages to load
      const totalMessages = response.data.total_messages || response.data.messages?.length || 0;
      setMessagesTotal(totalMessages);
      setHasMoreMessages(totalMessages > (response.data.messages?.length || 0));
      
      // Mark as read
      await axios.put(`${API_BASE_URL}/inbox/conversations/${conversationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update unread count in list
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, unread_count: 0 } : c
      ));
      
      // Scroll to bottom after loading
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
    } catch (error) {
      console.error('[INBOX] Failed to load conversation:', error);
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to load conversation: ${detail}`);
      // Rewind the mobile navigation so the user sees the list again
      // instead of a blank overlay stuck on an errored chat.
      if (isMobile) setShowMobileChat(false);
    } finally {
      setLoadingMessages(false);
    }
  };
  
  // Load more (older) messages
  const loadMoreMessages = async () => {
    if (!selectedConversation || loadingMoreMessages || !hasMoreMessages) return;
    
    setLoadingMoreMessages(true);
    try {
      const token = localStorage.getItem('token');
      const oldestMessage = messages[0];
      
      const response = await axios.get(
        `${API_BASE_URL}/inbox/conversations/${selectedConversation.id}/messages`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            limit: 50,
            before: oldestMessage?.timestamp
          }
        }
      );
      
      const olderMessages = response.data.messages || [];
      
      if (olderMessages.length > 0) {
        // Snapshot the user's scroll position BEFORE the prepend so
        // we can restore it AFTER React renders the new messages.
        // Without this, the messages list grows at the top by the
        // newly-prepended block's height, but scrollTop stays at 0,
        // visually yanking the user back to the new top. Combined
        // with `isPrependingRef`, this completely suppresses the
        // "jump back to bottom" bug.
        const container = messagesContainerRef.current;
        if (container) {
          scrollAnchorRef.current = container.scrollHeight - container.scrollTop;
        }
        isPrependingRef.current = true;
        setMessages(prev => [...olderMessages, ...prev]);
        setHasMoreMessages(response.data.has_more || false);
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      console.error('Failed to load more messages');
    } finally {
      setLoadingMoreMessages(false);
    }
  };
  
  // Refresh messages only (for polling - silent, no loading indicator)
  const refreshMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/inbox/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newMessages = response.data.messages || [];
      
      // Replace local state when the server has any message we don't have
      // yet — comparing by SET of ids is robust to reorderings (e.g. when
      // a newly-sent outbound sorts BEFORE an older inbound because of
      // a timezone normalisation fix). The previous "compare last id"
      // check silently swallowed updates whenever the new message landed
      // anywhere other than the tail of the list.
      setMessages(prev => {
        if (newMessages.length === 0) return prev;
        if (newMessages.length !== prev.length) return newMessages;
        const prevIds = new Set(prev.map(m => m.id));
        const hasNew = newMessages.some(m => !prevIds.has(m.id));
        return hasNew ? newMessages : prev;
      });
      
      // Update conversation's WhatsApp window status if changed
      if (response.data.conversation?.whatsapp_window) {
        setSelectedConversation(prev => ({
          ...prev,
          whatsapp_window: response.data.conversation.whatsapp_window
        }));
      }
    } catch (error) {
      // Silent fail for polling - don't show error toast
      console.error('Failed to refresh messages:', error);
    }
  }, []);
  
  // Send message
  const handleSendMessage = async () => {
    if (!selectedConversation) return;
    // Attachments selected on a WhatsApp chat → Enter sends them together
    // with any typed text as the caption (multiple files supported).
    if ((selectedFiles.length > 0 || selectedFile) && selectedConversation.channel === 'whatsapp') {
      await handleSendFile();
      return;
    }
    if (!newMessage.trim()) return;
    
    // Use Instagram send for Instagram channel
    if (selectedConversation.channel === 'instagram' || channelFilter === 'instagram') {
      await sendInstagramMessage();
      return;
    }
    
    // Use Email send for Email channel
    if (selectedConversation.channel === 'email' || channelFilter === 'email') {
      await handleSendEmail();
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      const messageData = { 
        content: newMessage, 
        message_type: 'text',
        sender_id: user.id,
        sender_name: user.full_name || user.email
      };
      
      // Add reply context if replying to a message
      if (replyToMessage) {
        // Use whatsapp_message_id (incoming) or wa_message_id (fallback) for the reply context
        const waMessageId = replyToMessage.whatsapp_message_id || replyToMessage.wa_message_id;
        if (waMessageId) {
          messageData.reply_to_message_id = waMessageId;
          messageData.reply_context = {
            message_id: waMessageId,
            content: replyToMessage.content?.slice(0, 100),
            sender_name: replyToMessage.direction === 'inbound' ? customer?.name : 'You'
          };
        } else {
          console.warn('Cannot quote reply: message does not have a WhatsApp message ID');
        }
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/inbox/conversations/${selectedConversation.id}/messages`,
        messageData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      setReplyToMessage(null); // Clear reply context
      
      // Update conversation in list
      setConversations(prev => prev.map(c => 
        c.id === selectedConversation.id 
          ? { ...c, last_message: newMessage.slice(0, 100), last_message_at: new Date().toISOString() }
          : c
      ));
      
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  // Handle quote reply
  const handleReplyToMessage = (message) => {
    setReplyToMessage(message);
    messageInputRef.current?.focus();
  };
  
  // Cancel reply
  const cancelReply = () => {
    setReplyToMessage(null);
  };
  
  // Handle message reaction
  const handleReaction = async (messageId, emoji) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/inbox/messages/${messageId}/reaction`,
        { emoji },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update message with reaction locally
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, reaction: emoji }
          : msg
      ));
      setShowReactionPicker(null);
      toast.success('Reaction sent');
    } catch (error) {
      toast.error('Failed to send reaction');
    }
  };
  
  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try to use audio/ogg with opus codec (WhatsApp compatible)
      // Fallback to audio/webm which may need server-side conversion
      let mimeType = 'audio/webm;codecs=opus';
      if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      }
      
      console.log('[RECORDING] Using mimeType:', mimeType);
      
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = async () => {
        // Use the actual recorded mimeType for the blob
        const actualMimeType = recorder.mimeType || mimeType;
        const audioBlob = new Blob(chunks, { type: actualMimeType });
        console.log('[RECORDING] Final blob type:', audioBlob.type, 'size:', audioBlob.size);
        await sendVoiceMessage(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Emit recording started indicator via WebSocket
      if (wsRef.current?.readyState === WebSocket.OPEN && selectedConversation) {
        wsRef.current.send(JSON.stringify({
          type: 'recording',
          conversation_id: selectedConversation.id,
          is_recording: true
        }));
      }
      
      // Start recording timer
      const timer = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      // Store timer ID to clear later
      recorder.timerInterval = timer;
    } catch (error) {
      toast.error('Microphone access denied');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      clearInterval(mediaRecorder.timerInterval);
      mediaRecorder.stop();
      setIsRecording(false);
      setRecordingTime(0);
      
      // Emit recording stopped indicator via WebSocket
      if (wsRef.current?.readyState === WebSocket.OPEN && selectedConversation) {
        wsRef.current.send(JSON.stringify({
          type: 'recording',
          conversation_id: selectedConversation.id,
          is_recording: false
        }));
      }
    }
  };
  
  const cancelRecording = () => {
    if (mediaRecorder) {
      clearInterval(mediaRecorder.timerInterval);
      mediaRecorder.stream?.getTracks().forEach(track => track.stop());
      setMediaRecorder(null);
      setIsRecording(false);
      setRecordingTime(0);
      
      // Emit recording stopped indicator via WebSocket
      if (wsRef.current?.readyState === WebSocket.OPEN && selectedConversation) {
        wsRef.current.send(JSON.stringify({
          type: 'recording',
          conversation_id: selectedConversation.id,
          is_recording: false
        }));
      }
    }
  };
  
  const sendVoiceMessage = async (audioBlob) => {
    if (!selectedConversation) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      
      // Determine the correct file extension based on blob type
      let filename = 'voice_message.ogg';
      if (audioBlob.type.includes('webm')) {
        filename = 'voice_message.webm';
      } else if (audioBlob.type.includes('mp4')) {
        filename = 'voice_message.mp4';
      } else if (audioBlob.type.includes('aac')) {
        filename = 'voice_message.aac';
      }
      
      console.log('[VOICE] Uploading audio:', filename, 'type:', audioBlob.type, 'size:', audioBlob.size);
      
      // Step 1: Upload audio to WhatsApp to get media_id
      const uploadFormData = new FormData();
      uploadFormData.append('file', audioBlob, filename);
      uploadFormData.append('conversation_id', selectedConversation.id);
      
      const uploadResponse = await axios.post(
        `${API_BASE_URL}/inbox/upload-media`,
        uploadFormData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      console.log('[VOICE] Upload response:', uploadResponse.data);
      
      if (!uploadResponse.data?.media_id) {
        throw new Error(uploadResponse.data?.detail || 'Failed to upload audio');
      }
      
      const { media_id, media_type, local_url } = uploadResponse.data;
      
      // Step 2: Send the audio message using the media_id
      const sendResponse = await axios.post(
        `${API_BASE_URL}/inbox/conversations/${selectedConversation.id}/send-media`,
        {
          media_id: media_id,
          media_type: media_type || 'audio',
          local_url: local_url
        },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      if (sendResponse.data) {
        setMessages(prev => [...prev, sendResponse.data]);
        toast.success('Voice message sent');
      }
    } catch (error) {
      console.error('Voice message error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send voice message');
    } finally {
      setSending(false);
    }
  };
  
  // Message search within conversation
  const handleMessageSearch = (query) => {
    setMessageSearch(query);
    if (!query.trim()) {
      setFilteredMessages([]);
      return;
    }
    
    const filtered = messages.filter(msg => 
      msg.content?.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredMessages(filtered);
  };
  
  // Scroll to searched message
  const scrollToMessage = (messageId) => {
    const element = document.getElementById(`message-${messageId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('bg-yellow-500/20');
      setTimeout(() => {
        element.classList.remove('bg-yellow-500/20');
      }, 2000);
    }
  };
  
  // Typing indicator
  const handleTyping = () => {
    if (!isTyping && selectedConversation) {
      setIsTyping(true);
      // Emit typing event via WebSocket
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'typing',
          conversation_id: selectedConversation.id
        }));
      }
    }
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set new timeout to stop typing indicator after 2 seconds
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 2000);
  };
  
  // Mark messages as read
  const markMessagesAsRead = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/inbox/conversations/${conversationId}/mark-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  };
  
  // Call markMessagesAsRead when conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      markMessagesAsRead(selectedConversation.id);
    }
  }, [selectedConversation?.id]);
  
  // Handle emoji selection
  const handleEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
    messageInputRef.current?.focus();
  };
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const accepted = [];
    for (const file of files) {
      // Determine file type and max size
      let maxSize = 16 * 1024 * 1024; // Default 16MB
      let fileType = 'document';
      if (file.type.startsWith('image/')) {
        fileType = 'image';
        maxSize = 5 * 1024 * 1024;
      } else if (file.type.startsWith('video/')) {
        fileType = 'video';
      } else if (file.type.startsWith('audio/')) {
        fileType = 'audio';
      } else {
        maxSize = 100 * 1024 * 1024;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: ${fileType} files must be less than ${Math.round(maxSize / (1024 * 1024))}MB`);
        continue;
      }
      const allowedTypes = {
        image: ['image/jpeg', 'image/png', 'image/webp'],
        video: ['video/mp4', 'video/3gpp'],
        audio: ['audio/aac', 'audio/mp4', 'audio/mpeg', 'audio/amr', 'audio/ogg'],
        document: []
      };
      const validTypes = allowedTypes[fileType] || [];
      if (validTypes.length > 0 && !validTypes.includes(file.type)) {
        toast.error(`${file.name}: unsupported ${fileType} format`);
        continue;
      }
      accepted.push(file);
    }
    e.target.value = '';
    if (!accepted.length) return;
    setSelectedFiles(prev => [...prev, ...accepted]);
    setSelectedFile(accepted[0]);
  };
  
  // Send file message
  const handleSendFile = async () => {
    if (!selectedFile || !selectedConversation) return;
    
    // Email conversations: send as a real email attachment via Gmail
    if (selectedConversation.channel === 'email') {
      if (selectedFile.size > 20 * 1024 * 1024) {
        toast.error('Attachment too large (max 20 MB for email)');
        return;
      }
      if (!selectedEmailAccount) {
        toast.error('No email account connected. Go to Integrations > Email.');
        return;
      }
      setUploadingFile(true);
      try {
        const token = localStorage.getItem('token');
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(selectedFile);
        });
        const toAddr = emailTo || selectedConversation.customer_email;
        if (!toAddr) { toast.error('No recipient email address'); setUploadingFile(false); return; }
        const subject = emailSubject || selectedConversation.email_subject || 'No Subject';
        const ccList = emailCc ? emailCc.split(',').map(x => x.trim()).filter(Boolean) : [];
        const lastInbound = [...messages].reverse().find(m => m.direction === 'inbound' && m.email_metadata?.message_id);
        const bodyText = newMessage || `Please find the attached file: ${selectedFile.name}`;
        await axios.post(`${API_BASE_URL}/email/send`, {
          account_id: selectedEmailAccount,
          to: [toAddr],
          subject,
          body_html: `<p>${bodyText.replace(/\n/g, '<br>')}</p>`,
          body_text: bodyText,
          cc: ccList.length > 0 ? ccList : null,
          in_reply_to: lastInbound?.email_metadata?.message_id || null,
          references: lastInbound?.email_metadata?.references || lastInbound?.email_metadata?.message_id || null,
          conversation_id: selectedConversation.id,
          attachments: [{
            filename: selectedFile.name,
            mime_type: selectedFile.type || 'application/octet-stream',
            content_base64: base64,
          }],
        }, { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
        toast.success('Email with attachment sent');
        setSelectedFile(null);
        setNewMessage('');
        refreshMessages(selectedConversation.id);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to send email attachment');
      } finally {
        setUploadingFile(false);
      }
      return;
    }
    
    // Check if this is a WhatsApp conversation
    if (selectedConversation.channel !== 'whatsapp') {
      toast.error('File sending is only supported for WhatsApp and Email conversations');
      return;
    }
    
    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const files = selectedFiles.length ? selectedFiles : [selectedFile];
      const caption = newMessage.trim();
      for (let idx = 0; idx < files.length; idx++) {
        const f = files[idx];
        let mediaType = 'document';
        if (f.type.startsWith('image/')) mediaType = 'image';
        else if (f.type.startsWith('video/')) mediaType = 'video';
        else if (f.type.startsWith('audio/')) mediaType = 'audio';

        const formData = new FormData();
        formData.append('file', f);
        formData.append('conversation_id', selectedConversation.id);
        const uploadResponse = await axios.post(
          `${API_BASE_URL}/inbox/upload-media`,
          formData,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
        );
        if (!uploadResponse.data.success) {
          throw new Error(uploadResponse.data.error || `Upload failed for ${f.name}`);
        }
        const response = await axios.post(
          `${API_BASE_URL}/inbox/conversations/${selectedConversation.id}/send-media`,
          { 
            media_id: uploadResponse.data.media_id,
            media_type: uploadResponse.data.media_type || mediaType,
            caption: idx === 0 ? caption : '',
            local_url: uploadResponse.data.local_url,
            sender_id: user.id,
            sender_name: user.full_name || user.email
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessages(prev => [...prev, response.data]);
      }
      setSelectedFile(null);
      setSelectedFiles([]);
      if (caption) setNewMessage('');
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      toast.success(files.length > 1 ? `${files.length} files sent` : 'File sent');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to send file';
      toast.error(errorMessage);
      console.error('File send error:', error);
    } finally {
      setUploadingFile(false);
    }
  };
  
  // Cancel file selection
  const handleCancelFile = () => {
    setSelectedFile(null);
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle paste event for images
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if the item is an image
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        
        const file = item.getAsFile();
        if (!file) continue;
        
        // Check file size (5MB limit for images)
        if (file.size > 5 * 1024 * 1024) {
          toast.error('Image must be less than 5MB');
          return;
        }
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (event) => {
          setPastedImagePreview(event.target.result);
        };
        reader.readAsDataURL(file);
        
        // Store the file
        setPastedImage(file);
        break;
      }
    }
  };

  // Send pasted image
  const handleSendPastedImage = async () => {
    if (!pastedImage || !selectedConversation) return;
    
    if (selectedConversation.channel !== 'whatsapp') {
      toast.error('Image sending is only supported for WhatsApp conversations');
      return;
    }
    
    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      
      // Upload to WhatsApp
      const formData = new FormData();
      formData.append('file', pastedImage, `pasted_image_${Date.now()}.png`);
      formData.append('conversation_id', selectedConversation.id);
      
      const uploadResponse = await axios.post(
        `${API_BASE_URL}/inbox/upload-media`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 60000
        }
      );
      
      if (!uploadResponse.data.success) {
        throw new Error(uploadResponse.data.error || 'Upload failed');
      }
      
      const localUrl = uploadResponse.data.local_url;  // Get local URL from upload response
      
      // Send the media message
      // Pass local_url so backend saves it to DB for other users
      const response = await axios.post(
        `${API_BASE_URL}/inbox/conversations/${selectedConversation.id}/send-media`,
        { 
          media_id: uploadResponse.data.media_id,
          media_type: 'image',
          caption: newMessage || '',
          local_url: localUrl  // Pass local_url so it gets saved to database
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Response should now have media_url from database
      setMessages(prev => [...prev, response.data]);
      
      // Clear pasted image and message
      setPastedImage(null);
      setPastedImagePreview(null);
      setNewMessage('');
      
      toast.success('Image sent');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to send image';
      toast.error(errorMessage);
    } finally {
      setUploadingFile(false);
    }
  };

  // Cancel pasted image
  const handleCancelPastedImage = () => {
    setPastedImage(null);
    setPastedImagePreview(null);
  };

  // Retry media download for failed messages
  const handleRetryMedia = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/inbox/messages/${messageId}/retry-media`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.media_url) {
        // Update the message in local state with new media_url
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, media_url: response.data.media_url }
            : msg
        ));
        toast.success('Media downloaded successfully');
      } else {
        toast.error(response.data.message || 'Failed to download media');
      }
    } catch (error) {
      toast.error('Failed to retry media download');
    }
  };
  
  // Assign conversation
  const handleAssign = async (userId, userName) => {
    if (!selectedConversation) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/inbox/conversations/${selectedConversation.id}/assign`,
        { user_id: userId, user_name: userName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSelectedConversation(prev => ({ ...prev, assigned_to: userId, assigned_to_name: userName }));
      setConversations(prev => prev.map(c => 
        c.id === selectedConversation.id 
          ? { ...c, assigned_to: userId, assigned_to_name: userName }
          : c
      ));
      
      toast.success(`Assigned to ${userName}`);
      setShowAssignDialog(false);
    } catch (error) {
      toast.error('Failed to assign conversation');
    }
  };
  
  // Toggle pin
  const handleTogglePin = async () => {
    if (!selectedConversation) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/inbox/conversations/${selectedConversation.id}/pin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSelectedConversation(prev => ({ ...prev, is_pinned: response.data.is_pinned }));
      setConversations(prev => prev.map(c => 
        c.id === selectedConversation.id 
          ? { ...c, is_pinned: response.data.is_pinned }
          : c
      ));
    } catch (error) {
      toast.error('Failed to update pin status');
    }
  };
  
  const handleMarkUnread = async (conv) => {
    if (!conv) return;
    try {
      const token = localStorage.getItem('token');
      const r = await axios.put(
        `${API_BASE_URL}/inbox/conversations/${conv.id}/unread`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(prev => prev.map(c =>
        c.id === conv.id ? { ...c, unread_count: r.data.unread_count || 1 } : c
      ));
      toast.success('Marked as unread');
    } catch (error) {
      toast.error('Failed to mark as unread');
    }
  };

  const handleToggleStar = async (conv) => {
    const target = conv || selectedConversation;
    if (!target) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/inbox/conversations/${target.id}/star`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedConversation(prev => (prev && prev.id === target.id
        ? { ...prev, is_starred: response.data.is_starred } : prev));
      setConversations(prev => prev.map(c =>
        c.id === target.id ? { ...c, is_starred: response.data.is_starred } : c
      ));
      toast.success(response.data.is_starred ? 'Marked as important' : 'Removed from important');
    } catch (error) {
      toast.error('Failed to update star');
    }
  };

  // Update status
  const handleUpdateStatus = async (status) => {
    if (!selectedConversation) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/inbox/conversations/${selectedConversation.id}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // If closing a chat and we're not in the 'closed' filter view, remove it from the list
      if (status === 'closed' && statusFilter !== 'closed') {
        setConversations(prev => prev.filter(c => c.id !== selectedConversation.id));
        setSelectedConversation(null);
        toast.success('Chat closed and archived');
      } else {
        setSelectedConversation(prev => ({ ...prev, status }));
        setConversations(prev => prev.map(c => 
          c.id === selectedConversation.id ? { ...c, status } : c
        ));
        toast.success(`Status updated to ${status}`);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  // Effects
  useEffect(() => {
    loadConversations();
    loadStats();
    loadTeamMembers();
    loadCannedResponses();
    loadTemplates();
    loadInstagramConversations();
    loadEmailAccounts();
    
    // Connect WebSocket
    connectWebSocket();
    
    // Cleanup on unmount
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [loadConversations, connectWebSocket]);
  
  // Handle channel filter change
  useEffect(() => {
    setSelectedConversation(null);
    setMessages([]);
    if (channelFilter === 'instagram') {
      loadInstagramConversations();
    } else if (channelFilter === 'email') {
      loadConversations(1);
    }
  }, [channelFilter]);

  // Handle assignment filter change
  useEffect(() => {
    // Only reload when assignmentFilter actually changes
    loadConversations(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentFilter]);
  
  // Watch conversation when selection OR ws-connected state changes.
  // Depending on `wsConnected` ensures we re-send `watch_conversation` after
  // the WS reconnects (e.g. after auto-reconnect), so incoming messages get
  // routed to the current chat.
  useEffect(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && selectedConversation) {
      wsRef.current.send(JSON.stringify({
        type: 'watch_conversation',
        conversation_id: selectedConversation.id
      }));
    }
  }, [selectedConversation, wsConnected]);
  
  // Newest `last_message_at` we've already heard a sound for. Anything
  // strictly newer that arrives via polling = "new inbound message
  // landed while the polling tab is open" → fire the sound. Initialise
  // on first conversation load (silent) so we don't spam the bell on
  // page-load.
  const newestKnownTimestampRef = useRef(null);

  // Whenever the conversation list changes, check if the newest
  // last_message_at advanced. This covers BOTH the WebSocket and
  // polling paths uniformly — even if WS goes down on a flaky
  // network, the user will still hear sound on poll-tick inbound
  // messages.
  useEffect(() => {
    if (!conversations || conversations.length === 0) return;
    let newest = null;
    let newestConv = null;
    for (const c of conversations) {
      const t = c.last_message_at ? Date.parse(c.last_message_at) : 0;
      if (t && (newest == null || t > newest)) {
        newest = t;
        newestConv = c;
      }
    }
    if (newest == null) return;
    const prev = newestKnownTimestampRef.current;
    if (prev == null) {
      // First load — seed without ringing.
      newestKnownTimestampRef.current = newest;
      return;
    }
    if (newest > prev) {
      newestKnownTimestampRef.current = newest;
      // Inbound message arrived (the WS handler also fires sound
      // separately for the case where we're already focused; this
      // path catches messages that came in via the polling refresh
      // when WS is disconnected — without this, sound was silent on
      // every channel for users behind sticky-less load balancers).
      playNotificationSound();
      // Desktop notif too (only fires when tab is backgrounded).
      if (newestConv) {
        const name = newestConv.customer_name || newestConv.customer_phone || 'Customer';
        const preview = (newestConv.last_message || '').slice(0, 140);
        showDesktopNotification(`New message from ${name}`, preview, newestConv.id);
      }
    }
  }, [conversations, playNotificationSound, showDesktopNotification]);

  // Fallback polling (more frequent for active chat, less for idle)
  useEffect(() => {
    // Faster polling when we have an active conversation open
    const pollInterval = wsConnected ? 30000 : (selectedConversation ? 3000 : 10000);
    
    const interval = setInterval(() => {
      if (!wsConnected) {
        // refresh=true preserves the user's "Load More" expansion so
        // appended pages don't vanish on every poll tick.
        loadConversations(1, false, true);
        loadStats();
        
        // Also refresh messages for the currently selected conversation
        if (selectedConversation) {
          refreshMessages(selectedConversation.id);
        }
      }
    }, pollInterval);
    
    return () => clearInterval(interval);
  }, [loadConversations, loadStats, wsConnected, selectedConversation, refreshMessages]);
  
  // Scroll to bottom when messages change — UNLESS this re-render
  // was caused by `loadMoreMessages` prepending older messages, in
  // which case we restore the user's previous scroll position
  // relative to the (now grown) bottom of the list. Without this
  // branch the user got yanked back to the bottom every time they
  // hit "Load older messages" — the exact symptom reported.
  useEffect(() => {
    if (isPrependingRef.current) {
      const container = messagesContainerRef.current;
      const anchor = scrollAnchorRef.current;
      if (container && anchor != null) {
        // After React rendered the new (older) messages the total
        // scrollHeight is larger; restore the user's distance from
        // the bottom so they remain on the same message they were
        // looking at when they clicked the button.
        container.scrollTop = container.scrollHeight - anchor;
      }
      isPrependingRef.current = false;
      scrollAnchorRef.current = null;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Keyboard shortcut for sending
  const handleKeyDown = (e) => {
    // Handle Enter to send message
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
      return;
    }
    
    // Handle Escape to close canned responses
    if (e.key === 'Escape' && showCannedResponses) {
      setShowCannedResponses(false);
    }
  };
  
  // Handle typing in message input
  const handleMessageInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // Auto-grow — match WhatsApp behaviour. Reset to the CSS min-height
    // first (clearing inline style) so the textarea can shrink back after
    // deletes, then grow to fit content up to a hard max (~160px ≈ 7 lines).
    // Uses scrollHeight which accounts for line-height + padding.
    const ta = e.target;
    if (ta && ta.style) {
      ta.style.height = 'auto';
      const next = Math.min(ta.scrollHeight, 160);
      ta.style.height = `${next}px`;
    }
    
    // Show canned responses when typing '/' at the beginning
    if (value === '/') {
      setShowCannedResponses(true);
    } else if (value.startsWith('/') && value.length > 1) {
      // Keep showing if searching through canned responses
      setShowCannedResponses(true);
    } else if (!value.startsWith('/')) {
      // Hide canned responses if text doesn't start with /
      setShowCannedResponses(false);
    }
    
    // Send typing indicator
    if (value.length > 0) {
      sendTypingIndicator(true);
    } else {
      sendTypingIndicator(false);
    }
  };
  
  // Send template message
  const handleSendTemplate = async () => {
    if (!selectedConversation || !selectedTemplate) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await axios.post(
        `${API_BASE_URL}/inbox/conversations/${selectedConversation.id}/send-template`,
        {
          template_name: selectedTemplate.name,
          template_params: templateParams,
          sender_id: user.id,
          sender_name: user.full_name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Note: WebSocket will handle adding the message to the list
      // But add it immediately for responsiveness if WS is slow
      if (response.data) {
        setMessages(prev => {
          if (prev.some(m => m.id === response.data.id)) return prev;
          return [...prev, response.data];
        });
      }
      
      setShowTemplateDialog(false);
      setSelectedTemplate(null);
      setTemplateParams([]);
      
      if (response.data.status === 'sent') {
        toast.success('Template message sent');
      } else {
        toast.error(`Failed to send: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('Failed to send template message');
    } finally {
      setSending(false);
    }
  };
  
  // Select template and prepare params
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    
    // Count placeholders in template body
    const body = template.components?.find(c => c.type === 'BODY')?.text || '';
    const bodyMatches = body.match(/\{\{\d+\}\}/g) || [];
    
    // Also check for button URL variables
    const buttonsComponent = template.components?.find(c => c.type === 'BUTTONS');
    let buttonUrlVars = 0;
    if (buttonsComponent?.buttons) {
      buttonsComponent.buttons.forEach(btn => {
        if (btn.type === 'URL' && btn.url?.includes('{{')) {
          buttonUrlVars++;
        }
      });
    }
    
    // Total params = body params + button URL params
    const totalParams = bodyMatches.length + buttonUrlVars;
    setTemplateParams(new Array(totalParams).fill(''));
  };
  
  // Handle canned response selection
  const handleCannedResponseSelect = (response) => {
    setNewMessage(response.content);
    setShowCannedResponses(false);
    messageInputRef.current?.focus();
  };
  
  // Separate pinned and unpinned conversations
  const pinnedConversations = conversations.filter(c => c.is_pinned);
  const unpinnedConversations = conversations.filter(c => !c.is_pinned);
  
  // Handle back button on mobile
  const handleMobileBack = () => {
    setShowMobileChat(false);
  };
  
  return (
    <div className="h-[calc(100vh-64px)] flex bg-wt-paper">
      {/* Left Panel - Conversation List */}
      <div className={`
        ${isMobile ? 'w-full' : 'w-80'} 
        border-r border-wt-border bg-wt-surface flex flex-col
        ${isMobile && showMobileChat ? 'hidden' : 'flex'}
      `}>
        {/* Header */}
        <div className="p-4 border-b border-wt-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-wt-text-primary">Inbox</h2>
            <div className="flex items-center gap-2">
              {/* Sound Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSound}
                className={`${soundEnabled ? 'text-wt-gold' : 'text-wt-text-muted'}`}
                title={soundEnabled ? 'Sound On - Click to disable' : 'Sound Off - Click to enable'}
                data-testid="sound-toggle-btn"
              >
                {soundEnabled ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/marketing/inbox/settings')}
                className="text-wt-text-muted hover:text-wt-gold"
                title="Inbox Settings"
                data-testid="inbox-settings-btn"
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { loadConversations(1); loadStats(); }}
                className="text-wt-text-muted"
                title="Refresh"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-3 mb-3 text-xs">
            <span className="flex items-center gap-1">
              <Circle className="h-2 w-2 fill-green-500 text-green-500" />
              {stats.open} Open
            </span>
            <span className="flex items-center gap-1">
              <Circle className="h-2 w-2 fill-yellow-500 text-yellow-500" />
              {stats.pending} Pending
            </span>
            <span className="flex items-center gap-1">
              <Circle className="h-2 w-2 fill-gray-500 text-gray-500" />
              {stats.unassigned} Unassigned
            </span>
          </div>
          
          {/* Channel Tabs */}
          <div className="flex items-center gap-1 mb-3 p-1 bg-wt-bg rounded-lg border border-wt-border overflow-hidden">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setChannelFilter('whatsapp')}
              className={`flex-1 min-w-0 px-2 transition-colors ${channelFilter === 'whatsapp'
                ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20'
                : 'text-wt-text-muted hover:text-wt-text-primary border border-transparent'}`}
              data-testid="whatsapp-channel-tab"
            >
              <MessageCircle className="h-4 w-4 mr-1 shrink-0" />
              <span className="truncate">WhatsApp</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setChannelFilter('instagram')}
              className={`flex-1 min-w-0 px-2 transition-colors ${channelFilter === 'instagram'
                ? 'bg-pink-500/15 text-pink-300 border border-pink-500/30 hover:bg-pink-500/20'
                : 'text-wt-text-muted hover:text-wt-text-primary border border-transparent'}`}
              data-testid="instagram-channel-tab"
            >
              <Instagram className="h-4 w-4 mr-1 shrink-0" />
              <span className="truncate">Instagram</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setChannelFilter('email')}
              className={`flex-1 min-w-0 px-2 transition-colors ${channelFilter === 'email'
                ? 'bg-wt-gold/15 text-wt-gold border border-wt-gold/30 hover:bg-wt-gold/20'
                : 'text-wt-text-muted hover:text-wt-text-primary border border-transparent'}`}
              data-testid="email-channel-tab"
            >
              <Mail className="h-4 w-4 mr-1 shrink-0" />
              <span className="truncate">Email</span>
            </Button>
          </div>
          
          {/* Compose Email Button (only for email channel) */}
          {channelFilter === 'email' && (
            <Button
              onClick={() => setComposeDialogOpen(true)}
              className="w-full bg-wt-gold text-black hover:bg-wt-gold/90 mt-2 font-medium"
              data-testid="compose-email-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Compose New Email
            </Button>
          )}
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, phone or order no..."
              className="pl-9 bg-wt-paper border-wt-border text-wt-text-primary"
            />
          </div>
          
          {/* Status Filter — pill row with per-tab count badge.
              Counts come from /inbox/stats (channel-scoped). */}
          <div className="flex items-center gap-1.5 mt-3 overflow-x-auto -mx-1 px-1 pb-1 scrollbar-thin">
            {[
              { id: 'all',      label: 'All',      countKey: 'all_visible' },
              { id: 'open',     label: 'Open',     countKey: 'open' },
              { id: 'pending',  label: 'Pending',  countKey: 'pending' },
              { id: 'resolved', label: 'Resolved', countKey: 'resolved' },
              { id: 'closed',   label: 'Closed',   countKey: 'closed' },
            ].map(({ id, label, countKey }) => {
              const isActive = statusFilter === id;
              const count = stats?.[countKey];
              return (
                <Button
                  key={id}
                  variant="ghost"
                  size="sm"
                  onClick={() => { setStatusFilter(id); setConversationPage(1); }}
                  className={`h-7 px-2.5 text-xs flex-shrink-0 transition-colors inline-flex items-center gap-1.5 ${isActive
                    ? 'bg-wt-gold text-black hover:bg-wt-gold/90'
                    : 'text-wt-text-muted hover:text-wt-text-primary hover:bg-wt-paper'
                  }`}
                  data-testid={`status-filter-${id}`}
                >
                  <span>{label}</span>
                  {typeof count === 'number' && (
                    <span
                      className={`inline-flex items-center justify-center min-w-[1.25rem] h-[1.1rem] px-1 rounded-full text-[10px] font-semibold leading-none ${isActive
                        ? 'bg-black/15 text-black'
                        : 'bg-wt-paper text-wt-text-muted border border-wt-border'
                      }`}
                      data-testid={`status-filter-${id}-count`}
                    >
                      {count > 999 ? '999+' : count}
                    </span>
                  )}
                </Button>
              );
            })}
            {/* Starred/Important filter — independent of status */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setStarredFilter(v => !v); setConversationPage(1); }}
              className={`h-7 px-2.5 text-xs flex-shrink-0 transition-colors inline-flex items-center gap-1 ${starredFilter
                ? 'bg-wt-gold text-black hover:bg-wt-gold/90'
                : 'text-wt-text-muted hover:text-wt-text-primary hover:bg-wt-paper'
              }`}
              data-testid="status-filter-starred"
            >
              <Star className={`h-3 w-3 ${starredFilter ? 'fill-black' : ''}`} />
              <span>Important</span>
            </Button>
          </div>
          
          {/* Assignment Filter */}
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            <span className="text-xs text-wt-text-muted mr-1">Assigned:</span>
            {[
              { id: 'all', label: 'All' },
              { id: 'mine', label: 'My Chats' },
              { id: 'unassigned', label: 'Unassigned' }
            ].map((filter) => (
              <Button
                key={filter.id}
                variant="ghost"
                size="sm"
                onClick={() => { setAssignmentFilter(filter.id); setConversationPage(1); }}
                className={`h-7 px-3 text-xs transition-colors ${assignmentFilter === filter.id
                  ? 'bg-wt-gold/15 text-wt-gold border border-wt-gold/30 hover:bg-wt-gold/20'
                  : 'text-wt-text-muted hover:text-wt-text-primary border border-transparent hover:bg-wt-paper'
                }`}
                data-testid={`assignment-filter-${filter.id}`}
              >
                {filter.id === 'mine' && <User className="h-3 w-3 mr-1" />}
                {filter.label}
              </Button>
            ))}
            <BurningLeadsButton
              onSelectConversation={(cid) => {
                const target = conversations.find(c => c.id === cid);
                if (target) setSelectedConversation(target);
              }}
            />
          </div>
        </div>
        
        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {channelFilter === 'whatsapp' || channelFilter === 'email' ? (
            // WhatsApp & Email Conversations (both use the same conversation API)
            <>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-wt-gold" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-wt-text-muted">
                  {channelFilter === 'email' ? (
                    <>
                      <Mail className="h-12 w-12 mb-3 opacity-30" />
                      <p>No email conversations yet</p>
                      <p className="text-xs mt-1">Connect an email account in Integrations to start</p>
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-12 w-12 mb-3 opacity-30" />
                      <p>No WhatsApp conversations yet</p>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {pinnedConversations.length > 0 && (
                    <>
                      <div className="px-4 py-2 text-xs font-medium text-wt-text-muted flex items-center gap-2">
                        <Pin className="h-3 w-3" />
                        PINNED
                      </div>
                      {pinnedConversations.map((conv) => (
                        <ChatListItem
                          key={conv.id}
                          conversation={conv}
                          isSelected={selectedConversation?.id === conv.id}
                          onClick={() => loadConversationDetails(conv.id)}
                          onToggleStar={handleToggleStar}
                          onMarkUnread={handleMarkUnread}
                        />
                      ))}
                    </>
                  )}
                  
                  {unpinnedConversations.length > 0 && (
                    <>
                      <div className="px-4 py-2 text-xs font-medium text-wt-text-muted flex items-center justify-between">
                        <span>ALL CHATS ({conversationTotal})</span>
                        <span className="text-[10px]">{conversations.length} loaded</span>
                      </div>
                      {unpinnedConversations.map((conv) => (
                        <ChatListItem
                          key={conv.id}
                          conversation={conv}
                          isSelected={selectedConversation?.id === conv.id}
                          onClick={() => loadConversationDetails(conv.id)}
                          onToggleStar={handleToggleStar}
                          onMarkUnread={handleMarkUnread}
                        />
                      ))}
                    </>
                  )}
                  
                  {/* Load More Conversations Button */}
                  {conversations.length < conversationTotal && (
                    <div className="p-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-wt-border text-wt-text-muted hover:border-wt-gold hover:text-wt-gold"
                        onClick={loadMoreConversations}
                        data-testid="load-more-conversations-btn"
                      >
                        Load More ({conversationTotal - conversations.length} remaining)
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            // Instagram Conversations
            <>
              {loadingInstagram ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-pink-500" />
                </div>
              ) : instagramConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-wt-text-muted">
                  <Instagram className="h-12 w-12 mb-3 opacity-30" />
                  <p>No Instagram conversations yet</p>
                  <p className="text-xs mt-1">DMs will appear here once received</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4"
                    onClick={syncInstagramConversations}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync from Instagram
                  </Button>
                </div>
              ) : (
                <>
                  <div className="px-4 py-2 text-xs font-medium text-wt-text-muted flex items-center justify-between">
                    <span className="flex items-center gap-1">
                      <Instagram className="h-3 w-3" />
                      INSTAGRAM DMs ({instagramConversations.length})
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2"
                      onClick={syncInstagramConversations}
                      disabled={loadingInstagram}
                    >
                      <RefreshCw className={`h-3 w-3 ${loadingInstagram ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  {instagramConversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => {
                        loadInstagramMessages(conv.id);
                        if (isMobile) setShowMobileChat(true);
                      }}
                      className={`p-3 cursor-pointer border-b border-wt-border hover:bg-wt-bg transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-wt-bg border-l-2 border-l-pink-500' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          {conv.participant_profile_pic ? (
                            <img src={conv.participant_profile_pic} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-sm">
                              {conv.participant_username?.slice(0, 2).toUpperCase() || 'IG'}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-wt-text-primary truncate">
                              @{conv.participant_username || 'Instagram User'}
                            </span>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-pink-500 text-white text-xs ml-2">
                                {conv.unread_count}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-wt-text-muted truncate">
                            {conv.last_message || 'No messages'}
                          </p>
                          <p className="text-xs text-wt-text-muted mt-1">
                            {conv.last_message_at && new Date(conv.last_message_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Center Panel - Chat */}
      <div className={`
        flex-1 flex flex-col min-w-0
        ${isMobile && !showMobileChat ? 'hidden' : 'flex'}
        ${isMobile ? 'fixed inset-0 z-50 bg-wt-paper overflow-hidden' : ''}
      `}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-3 md:p-4 border-b border-wt-border bg-wt-surface flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                {/* Mobile Back Button */}
                {isMobile && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMobileBack}
                    className="text-wt-text-primary p-1 -ml-1"
                  >
                    <ArrowLeft className="h-6 w-6" />
                  </Button>
                )}
                <Avatar className="h-9 w-9 md:h-10 md:w-10 flex-shrink-0">
                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-sm">
                    {selectedConversation.customer_name?.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-wt-text-primary truncate">
                      {selectedConversation.customer_name}
                    </span>
                    <div className={`h-2 w-2 rounded-full flex-shrink-0 ${STATUS_COLORS[selectedConversation.status]}`} />
                    {/* WhatsApp 24h Window Timer */}
                    {selectedConversation.channel === 'whatsapp' && selectedConversation.whatsapp_window && (
                      <WhatsAppWindowTimer 
                        whatsappWindow={selectedConversation.whatsapp_window} 
                        compact={isMobile}
                      />
                    )}
                  </div>
                  <div className="flex items-center gap-1 overflow-hidden">
                    {selectedConversation.channel === 'email' && selectedConversation.email_subject && (
                      <span className="text-xs text-wt-text-muted truncate">
                        {selectedConversation.email_subject}
                      </span>
                    )}
                    {selectedConversation.channel === 'email' && selectedConversation.customer_email && (
                      <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                        {selectedConversation.customer_email}
                      </Badge>
                    )}
                    {selectedConversation.tags?.slice(0, 2).map((tag, idx) => {
                      // ``tags`` is heterogeneous — sometimes [{tag:"..."}],
                      // sometimes ["string"]. Normalize to a string so
                      // ``.toLowerCase()`` never blows up (this was the
                      // root cause of the blank-screen crash on 2nd-chat
                      // open when that chat had tags).
                      const tagStr = typeof tag === 'string' ? tag : (tag?.tag || tag?.name || '');
                      if (!tagStr) return null;
                      return (
                        <Badge
                          key={idx}
                          className={`text-xs ${TAG_COLORS[tagStr.toLowerCase().replace(/\s+/g, '_')] || 'bg-gray-500'} text-white`}
                        >
                          {tagStr}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                {/* Search Messages Button */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowMessageSearch(!showMessageSearch)}
                  className={`text-wt-text-muted h-8 w-8 p-0 ${showMessageSearch ? 'bg-wt-paper text-wt-gold' : ''}`}
                >
                  <Search className="h-4 w-4" />
                </Button>
                
                {/* Create Order from conversation — reuses the existing
                    Orders create flow with customer prefilled */}
                <Button
                  variant="outline"
                  size="sm"
                  className="border-wt-gold/40 text-wt-gold h-8 px-2 md:px-3"
                  data-testid="inbox-create-order-btn"
                  title="Create order for this customer (you become the Sales Owner)"
                  onClick={() => {
                    const name = (selectedConversation.customer_name || '').trim();
                    const parts = name && !/^\+?\d+$/.test(name) ? name.split(/\s+/) : [];
                    let digits = String(selectedConversation.customer_phone || '').replace(/[^\d]/g, '');
                    let cc = 'AE';
                    if (digits.startsWith('971')) { digits = digits.slice(3); cc = 'AE'; }
                    else if (digits.startsWith('91')) { digits = digits.slice(2); cc = 'IN'; }
                    sessionStorage.setItem('inbox_create_order', JSON.stringify({
                      first_name: parts[0] || '',
                      last_name: parts.slice(1).join(' ') || '',
                      customer_email: selectedConversation.customer_email || '',
                      phone_country_code: cc,
                      phone_number: digits,
                      conversation_id: selectedConversation.id,
                      lead_id: selectedConversation.lead_id || null,
                    }));
                    navigate('/orders');
                  }}
                >
                  <ShoppingCart className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">Create Order</span>
                </Button>

                {/* Lead stage quick update (auto-linked lead) */}
                {selectedConversation.lead_id && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="border-wt-border text-wt-text-primary h-8 px-2 md:px-3" data-testid="inbox-lead-stage-btn" title="Update linked lead stage">
                        <Target className="h-4 w-4 md:mr-2" />
                        <span className="hidden md:inline">Lead</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-wt-surface border-wt-border">
                      {[['new', 'New'], ['contacted', 'Contacted'], ['quotation_sent', 'Quotation Sent'], ['negotiation', 'Negotiation'], ['closed_won', 'Closed Won'], ['closed_lost', 'Closed Lost']].map(([val, label]) => (
                        <DropdownMenuItem
                          key={val}
                          className="text-wt-text-primary hover:bg-wt-paper"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              await axios.put(`${API_BASE_URL}/leads/${selectedConversation.lead_id}`, { stage: val }, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success(`Lead stage → ${label}`);
                            } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update lead'); }
                          }}
                        >
                          {label}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuItem className="text-wt-gold hover:bg-wt-paper" onClick={() => navigate(`/leads/${selectedConversation.lead_id}`)}>
                        Open Lead
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Assign Button - icon only on mobile */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="border-wt-border text-wt-text-primary h-8 px-2 md:px-3">
                      <UserPlus className="h-4 w-4 md:mr-2" />
                      <span className="hidden md:inline">{selectedConversation.assigned_to_name || 'Assign'}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-wt-surface border-wt-border">
                    {teamMembers.map((member) => (
                      <DropdownMenuItem
                        key={member.id}
                        onClick={() => handleAssign(member.id, member.full_name)}
                        className="text-wt-text-primary hover:bg-wt-paper"
                      >
                        {member.full_name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* More Options */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-wt-text-muted h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-wt-surface border-wt-border" align="end">
                    <DropdownMenuItem
                      onClick={() => window.open('/product-availability', '_blank')}
                      className="text-wt-gold hover:bg-wt-paper"
                      data-testid="inbox-check-availability"
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Check Product Availability
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleTogglePin}
                      className="text-wt-text-primary hover:bg-wt-paper"
                    >
                      <Pin className="h-4 w-4 mr-2" />
                      {selectedConversation.is_pinned ? 'Unpin' : 'Pin'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleToggleStar()}
                      className="text-wt-text-primary hover:bg-wt-paper"
                      data-testid="conversation-star-toggle"
                    >
                      <Star className={`h-4 w-4 mr-2 ${selectedConversation.is_starred ? 'text-wt-gold fill-wt-gold' : ''}`} />
                      {selectedConversation.is_starred ? 'Remove from Important' : 'Mark as Important'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-wt-border" />
                    <DropdownMenuItem
                      onClick={() => handleUpdateStatus('resolved')}
                      className="text-wt-text-primary hover:bg-wt-paper"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Mark Resolved
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleUpdateStatus('closed')}
                      className="text-wt-text-primary hover:bg-wt-paper"
                    >
                      <Archive className="h-4 w-4 mr-2" />
                      Close
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* Customer Panel Toggle - Hidden on mobile */}
                {!isMobile && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCustomerPanel(!showCustomerPanel)}
                    className={showCustomerPanel ? 'text-wt-gold' : 'text-wt-text-muted'}
                  >
                    <User className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            
            {/* Message Search Bar */}
            {showMessageSearch && (
              <div className="border-b border-wt-border bg-wt-surface p-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
                  <Input
                    placeholder="Search in conversation..."
                    value={messageSearch}
                    onChange={(e) => handleMessageSearch(e.target.value)}
                    className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                  />
                </div>
                {/* Search Results */}
                {messageSearch && filteredMessages.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto">
                    <p className="text-xs text-wt-text-muted mb-2">
                      {filteredMessages.length} message{filteredMessages.length !== 1 ? 's' : ''} found
                    </p>
                    {filteredMessages.map((msg) => (
                      <button
                        key={msg.id}
                        onClick={() => scrollToMessage(msg.id)}
                        className="w-full text-left p-2 rounded hover:bg-wt-paper text-sm text-wt-text-primary truncate"
                      >
                        {msg.content?.slice(0, 60)}...
                      </button>
                    ))}
                  </div>
                )}
                {messageSearch && filteredMessages.length === 0 && (
                  <p className="text-xs text-wt-text-muted mt-2">No messages found</p>
                )}
              </div>
            )}
            
            {/* Messages */}
            <div
              ref={messagesContainerRef}
              className={`flex-1 overflow-y-auto p-4 bg-wt-paper ${isMobile ? 'pb-20' : ''}`}
              data-testid="inbox-messages-scroll"
            >
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-wt-text-muted">
                  <MessageCircle className="h-16 w-16 mb-4 opacity-30" />
                  <p>No messages yet</p>
                  <p className="text-sm">Start the conversation!</p>
                </div>
              ) : (
                <>
                  {/* Load More Messages Button */}
                  {hasMoreMessages && (
                    <div className="flex justify-center py-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={loadMoreMessages}
                        disabled={loadingMoreMessages}
                        className="text-wt-text-muted hover:text-wt-gold text-xs"
                        data-testid="load-more-messages-btn"
                      >
                        {loadingMoreMessages ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin mr-2" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-3 w-3 mr-1 rotate-180" />
                            Load older messages
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {messages.map((msg, idx) => {
                    const dayOf = (ts) => (ts ? new Date(ts).toDateString() : null);
                    const msgDay = dayOf(msg.timestamp);
                    const prevDay = idx > 0 ? dayOf(messages[idx - 1].timestamp) : null;
                    const showDateChip = msgDay && msgDay !== prevDay;
                    const chipLabel = (() => {
                      if (!showDateChip) return null;
                      const d = new Date(msg.timestamp);
                      const today = new Date();
                      const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
                      if (d.toDateString() === today.toDateString()) return 'Today';
                      if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
                      return d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
                    })();
                    return (
                      <React.Fragment key={msg.id}>
                        {showDateChip && (
                          <div className="flex justify-center my-3" data-testid="chat-date-separator">
                            <span className="px-3 py-1 rounded-full text-[11px] bg-wt-paper border border-wt-border text-wt-text-muted">
                              {chipLabel}
                            </span>
                          </div>
                        )}
                        <MessageBubble
                          message={msg}
                          isOutbound={msg.direction === 'outbound'}
                          onRetryMedia={handleRetryMedia}
                          onReply={handleReplyToMessage}
                          onReact={handleReaction}
                          showReactionPicker={showReactionPicker}
                          setShowReactionPicker={setShowReactionPicker}
                          reactionEmojis={REACTION_EMOJIS}
                          conversationId={selectedConversation?.id}
                        />
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
            
            {/* Message Input */}
            <div className={`border-t border-wt-border bg-wt-surface ${isMobile ? 'sticky bottom-0 left-0 right-0 z-20' : ''}`}>
              {/* WhatsApp Window Expired Banner */}
              {selectedConversation.channel === 'whatsapp' && 
               selectedConversation.whatsapp_window && 
               !selectedConversation.whatsapp_window.is_window_open && (
                <WhatsAppWindowExpiredBanner 
                  whatsappWindow={selectedConversation.whatsapp_window}
                  onSendTemplate={() => setShowTemplateDialog(true)}
                />
              )}
              
              <div className="p-4">
              {/* File Preview */}
              {(selectedFiles.length > 0 || selectedFile) && (
                <div className="mb-3 p-3 bg-wt-paper rounded-lg border border-wt-border space-y-2" data-testid="attachment-preview">
                  {(selectedFiles.length ? selectedFiles : [selectedFile]).map((f, i) => (
                    <div key={`${f.name}-${i}`} className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        {f.type.startsWith('image/') ? (
                          <ImageIcon className="h-6 w-6 text-blue-400 flex-shrink-0" />
                        ) : f.type.startsWith('video/') ? (
                          <Video className="h-6 w-6 text-purple-400 flex-shrink-0" />
                        ) : (
                          <File className="h-6 w-6 text-gray-400 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-wt-text-primary truncate max-w-[200px]">{f.name}</p>
                          <p className="text-xs text-wt-text-muted">{(f.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300"
                        onClick={() => {
                          const rest = (selectedFiles.length ? selectedFiles : [selectedFile]).filter((_, x) => x !== i);
                          setSelectedFiles(rest);
                          setSelectedFile(rest[0] || null);
                        }} data-testid={`remove-attachment-${i}`}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-1 border-t border-wt-border/50">
                    <p className="text-[11px] text-wt-text-muted">Type a caption below — Enter or Send sends {selectedFiles.length > 1 ? 'all files' : 'the file'} + text together</p>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={handleCancelFile} className="text-red-400 hover:text-red-300">Clear all</Button>
                      <Button size="sm" onClick={handleSendFile} disabled={uploadingFile}
                        className="bg-wt-gold hover:bg-wt-gold-dark text-black" data-testid="send-attachments-btn">
                        {uploadingFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Reply Preview */}
              {replyToMessage && (
                <div className="mb-3 p-3 bg-wt-paper rounded-lg border-l-4 border-wt-gold flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-wt-gold font-medium mb-1">
                      Replying to {replyToMessage.direction === 'inbound' ? selectedConversation?.customer_name : 'yourself'}
                    </p>
                    <p className="text-sm text-wt-text-muted truncate">
                      {replyToMessage.content?.slice(0, 100)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={cancelReply}
                    className="text-wt-text-muted hover:text-red-400 ml-2"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              
              {/* Voice Recording UI */}
              {isRecording && (
                <div className="mb-3 p-3 bg-red-500/10 rounded-lg border border-red-500/30 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-red-400 font-medium">Recording...</span>
                    <span className="text-wt-text-muted text-sm font-mono">
                      {Math.floor(recordingTime / 60).toString().padStart(2, '0')}:
                      {(recordingTime % 60).toString().padStart(2, '0')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={cancelRecording}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={stopRecording}
                      className="bg-wt-gold hover:bg-wt-gold-dark text-black"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="flex items-end gap-1 sm:gap-2">
                {/* Attachment Button */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-wt-text-muted mb-1 hover:text-wt-text-primary px-2 shrink-0">
                      <Paperclip className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-wt-surface border-wt-border" align="start">
                    <DropdownMenuItem
                      onClick={() => {
                        fileInputRef.current?.setAttribute('accept', 'image/*');
                        fileInputRef.current?.click();
                      }}
                      className="text-wt-text-primary hover:bg-wt-paper cursor-pointer"
                    >
                      <ImageIcon className="h-4 w-4 mr-2 text-blue-400" />
                      Image
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        fileInputRef.current?.setAttribute('accept', 'video/*');
                        fileInputRef.current?.click();
                      }}
                      className="text-wt-text-primary hover:bg-wt-paper cursor-pointer"
                    >
                      <Video className="h-4 w-4 mr-2 text-purple-400" />
                      Video
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        fileInputRef.current?.setAttribute('accept', '.pdf,.doc,.docx,.xls,.xlsx,.txt');
                        fileInputRef.current?.click();
                      }}
                      className="text-wt-text-primary hover:bg-wt-paper cursor-pointer"
                    >
                      <FileText className="h-4 w-4 mr-2 text-orange-400" />
                      Document
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  onChange={handleFileSelect}
                />
                
                {/* Canned Responses — hidden on mobile (user can type "/" to trigger) */}
                <Popover open={showCannedResponses} onOpenChange={setShowCannedResponses}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="hidden sm:inline-flex text-wt-text-muted mb-1 hover:text-wt-text-primary"
                      title="Quick Replies (or type / to open)"
                      data-testid="canned-responses-btn"
                    >
                      <MessageCircle className="h-5 w-5" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-80 p-2 bg-wt-surface border-wt-border max-h-64 overflow-y-auto" 
                    align="start"
                    side="top"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                  >
                    <p className="text-xs font-medium text-wt-text-muted px-2 py-1">
                      Quick Replies {newMessage.startsWith('/') && <span className="text-wt-gold">(type to filter)</span>}
                    </p>
                    {cannedResponses.length === 0 ? (
                      <p className="text-sm text-wt-text-muted text-center py-4">
                        No canned responses. <br />
                        <button 
                          onClick={() => navigate('/marketing/inbox/settings')}
                          className="text-wt-gold hover:underline"
                        >
                          Add some in settings
                        </button>
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {cannedResponses
                          .filter(response => {
                            // Filter based on search term after '/'
                            if (newMessage.startsWith('/') && newMessage.length > 1) {
                              const searchTerm = newMessage.slice(1).toLowerCase();
                              return response.title.toLowerCase().includes(searchTerm) ||
                                     response.content.toLowerCase().includes(searchTerm);
                            }
                            return true;
                          })
                          .map((response) => (
                          <button
                            key={response.id}
                            onClick={() => handleCannedResponseSelect(response)}
                            className="w-full text-left p-2 rounded hover:bg-wt-paper transition-colors"
                            data-testid={`canned-response-${response.id}`}
                          >
                            <span className="font-medium text-sm text-wt-text-primary block truncate">
                              {response.title}
                            </span>
                            <span className="text-xs text-wt-text-muted line-clamp-2">
                              {response.content}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
                
                {/* Template Messages Button — hidden on mobile (accessible
                    via the attachment "+" menu for clean composer) */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowTemplateDialog(true)}
                  className="hidden sm:inline-flex text-wt-text-muted mb-1 hover:text-wt-text-primary"
                  title="Send Template (for messages outside 24h window)"
                  data-testid="send-template-btn"
                  style={{ display: selectedConversation?.channel === 'email' ? 'none' : undefined }}
                >
                  <FileText className="h-5 w-5" />
                </Button>
                
                <div className="flex-1 min-w-0">
                  {selectedConversation?.channel === 'email' && (
                    <div className="space-y-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-wt-text-muted w-12">To:</span>
                        <Input
                          value={emailTo || selectedConversation.customer_email || ''}
                          onChange={e => setEmailTo(e.target.value)}
                          placeholder="recipient@example.com"
                          className="h-7 text-sm bg-wt-paper border-wt-border text-wt-text-primary"
                          data-testid="email-to-input"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-wt-text-muted w-12">Subject:</span>
                        <Input
                          value={emailSubject || (selectedConversation.email_subject ? `Re: ${selectedConversation.email_subject}` : '')}
                          onChange={e => setEmailSubject(e.target.value)}
                          placeholder="Email subject"
                          className="h-7 text-sm bg-wt-paper border-wt-border text-wt-text-primary"
                          data-testid="email-subject-input"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-wt-text-muted w-12">Cc:</span>
                        <Input
                          value={emailCc}
                          onChange={e => setEmailCc(e.target.value)}
                          placeholder="cc@example.com (comma-separated)"
                          className="h-7 text-sm bg-wt-paper border-wt-border text-wt-text-primary"
                          data-testid="email-cc-input"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Pasted Image Preview */}
                  {pastedImagePreview && (
                    <div className="mb-2 relative inline-block">
                      <img 
                        src={pastedImagePreview} 
                        alt="Pasted" 
                        className="max-h-32 rounded-lg border border-wt-border"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelPastedImage}
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full bg-red-500 hover:bg-red-600 text-white"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  {/* AI Sales Copilot — Session 4 reply suggestion panel.
                      Conditionally renders only when the tenant has the
                      Copilot enabled in `ai_copilot_settings`. */}
                  {selectedConversation && (
                    <>
                      {/* Handoff banner — renders ONLY when the conversation
                          is actively flagged (thin red strip). Hidden state
                          contributes nothing — the "Request handoff" chip
                          lives inside the toolbar below. */}
                      {selectedConversation.needs_human_handoff && (
                        <AICopilotHandoffBanner
                          conversation={selectedConversation}
                          onChange={updated => setSelectedConversation(updated)}
                        />
                      )}
                      {/* Session 6: abandoned cart, product recs, follow-up.
                          Self-hides when there's nothing to show. */}
                      <AICopilotSession6Panel
                        conversationId={selectedConversation.id}
                        customerPhone={selectedConversation.customer_phone || selectedConversation.phone_number}
                        customerEmail={selectedConversation.customer_email}
                        onInsertSuggestion={(text) => {
                          setNewMessage(text);
                          setTimeout(() => messageInputRef.current?.focus(), 0);
                        }}
                      />
                      {/* UNIFIED AI TOOLBAR — single horizontal row. All
                          sub-components use display:contents so their
                          buttons are direct children of this flex-wrap
                          container. Result: one tight row that wraps
                          gracefully on narrow screens. */}
                      <div className="ai-toolbar flex flex-wrap items-center gap-x-1.5 gap-y-1 mb-1.5 pt-1"
                           data-testid="ai-copilot-toolbar">
                        <ReplySuggestionPanel
                          conversationId={selectedConversation.id}
                          authToken={localStorage.getItem('token')}
                          onInsertSuggestion={(text) => {
                            setNewMessage(text);
                            setTimeout(() => messageInputRef.current?.focus(), 0);
                          }}
                        />
                        <AICopilotInboxExtras conversation={selectedConversation} />
                        <AICopilotComposerTools
                          text={newMessage}
                          onReplace={(t) => {
                            setNewMessage(t);
                            setTimeout(() => messageInputRef.current?.focus(), 0);
                          }}
                        />
                        {/* Handoff chip — last, so it sits to the right
                            of the rest. Only renders when handoff is NOT
                            already active (in which case the banner shows). */}
                        {!selectedConversation.needs_human_handoff && (
                          <AICopilotHandoffBanner
                            conversation={selectedConversation}
                            onChange={updated => setSelectedConversation(updated)}
                          />
                        )}
                      </div>
                    </>
                  )}
                  <div className="relative">
                    <Textarea
                      ref={messageInputRef}
                      value={newMessage}
                      onChange={handleMessageInputChange}
                      onKeyDown={handleKeyDown}
                      onBlur={() => sendTypingIndicator(false)}
                      onPaste={handlePaste}
                      placeholder={pastedImagePreview ? "Add a caption (optional)..." : "Type a message…"}
                      className="min-h-[44px] sm:min-h-[44px] max-h-40 bg-wt-paper border-wt-border text-wt-text-primary resize-none rounded-3xl sm:rounded-md pr-11 sm:pr-3 py-3 sm:py-3 overflow-hidden"
                      rows={1}
                      data-testid="chat-message-input"
                    />
                    {/* Emoji button — absolutely positioned INSIDE the pill on
                        mobile (WhatsApp parity). On desktop it's hidden here
                        because the sibling emoji button below is visible. */}
                    <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="sm:hidden absolute right-2 bottom-2 p-1.5 text-wt-text-muted hover:text-wt-text-primary"
                          data-testid="chat-emoji-btn-mobile"
                        >
                          <Smile className="h-5 w-5" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-72 p-2 bg-wt-surface border-wt-border"
                        align="end"
                        side="top"
                      >
                        <div className="grid grid-cols-8 gap-1">
                          {EMOJI_LIST.map((emoji, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleEmojiSelect(emoji)}
                              className="p-1.5 hover:bg-wt-paper rounded text-xl transition-colors"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {/* Typing Indicator */}
                  {selectedConversation && typingUsers[selectedConversation.id] && 
                   Object.keys(typingUsers[selectedConversation.id]).length > 0 && (
                    <div className="text-xs text-wt-text-muted mt-1 flex items-center gap-1">
                      <span className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-wt-gold rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-1.5 h-1.5 bg-wt-gold rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-1.5 h-1.5 bg-wt-gold rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </span>
                      {Object.values(typingUsers[selectedConversation.id]).join(', ')} typing...
                    </div>
                  )}
                  {/* Recording Indicator */}
                  {selectedConversation && recordingUsers[selectedConversation.id] && 
                   Object.keys(recordingUsers[selectedConversation.id]).length > 0 && (
                    <div className="text-xs text-red-400 mt-1 flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      {Object.values(recordingUsers[selectedConversation.id]).join(', ')} recording voice message...
                    </div>
                  )}
                </div>
                
                {/* Emoji Picker */}
                <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="hidden sm:inline-flex text-wt-text-muted mb-1 hover:text-wt-text-primary">
                      <Smile className="h-5 w-5" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-72 p-2 bg-wt-surface border-wt-border" 
                    align="end"
                    side="top"
                  >
                    <div className="grid grid-cols-8 gap-1">
                      {EMOJI_LIST.map((emoji, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleEmojiSelect(emoji)}
                          className="p-1.5 hover:bg-wt-paper rounded text-xl transition-colors"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                
                {/* Voice Record Button */}
                {!isRecording && !newMessage.trim() && !pastedImage && (
                  <Button 
                    variant="ghost" 
                    onClick={startRecording}
                    className="text-wt-text-muted mb-1 hover:text-wt-gold"
                    title="Record voice message"
                    data-testid="voice-record-btn"
                  >
                    <Mic className="h-5 w-5" />
                  </Button>
                )}
                
                <Button
                  onClick={pastedImage ? handleSendPastedImage : handleSendMessage}
                  disabled={(!newMessage.trim() && !pastedImage && !selectedFile && selectedFiles.length === 0) || sending || uploadingFile}
                  className="bg-wt-gold hover:bg-wt-gold-dark text-black mb-1"
                >
                  {(sending || uploadingFile) ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </Button>
              </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-wt-text-muted p-4">
            {/* Mobile back arrow so a stuck loader/error doesn't trap the user */}
            {isMobile && showMobileChat && (
              <button
                onClick={handleMobileBack}
                className="absolute top-3 left-3 p-2 text-wt-text-primary"
                data-testid="mobile-chat-back-empty"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
            )}
            {loadingMessages ? (
              <>
                <Loader2 className="h-12 w-12 mb-4 animate-spin text-wt-gold" />
                <p className="text-sm">Loading conversation…</p>
              </>
            ) : (
              <>
                <MessageCircle className="h-24 w-24 mb-6 opacity-20" />
                <h3 className="text-xl font-medium text-wt-text-primary mb-2">Select a conversation</h3>
                <p className="text-sm text-center">Choose a chat from the list to start messaging</p>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Right Panel - Customer Info (Hidden on mobile) */}
      {showCustomerPanel && selectedConversation && !isMobile && (
        <CustomerInfoPanel
          conversation={selectedConversation}
          customer={customer}
          orders={orders}
          onClose={() => setShowCustomerPanel(false)}
          formatCurrency={formatCurrency}
        />
      )}
      
      {/* WebSocket Status Indicator — hidden on mobile (was overlapping the
          send button at bottom-right of the composer). Still visible on
          desktop where there's screen real estate. */}
      <div className="hidden sm:block fixed bottom-4 right-4 z-50">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
          wsConnected 
            ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
            : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
        }`}>
          <div className={`h-2 w-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-blue-500'}`} />
          {wsConnected ? 'Live' : 'Polling'}
        </div>
      </div>
      
      {/* Template Message Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Send Template Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-wt-text-muted">
              Use templates to send messages outside the 24-hour conversation window.
            </p>
            
            {templates.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto text-wt-text-muted mb-3" />
                <p className="text-wt-text-muted">No approved templates available</p>
                <p className="text-sm text-wt-text-muted mt-1">
                  Create templates in your WhatsApp Business Manager
                </p>
              </div>
            ) : !selectedTemplate ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {templates.map((template) => (
                  <button
                    key={template.name}
                    onClick={() => handleSelectTemplate(template)}
                    className="w-full text-left p-3 rounded-lg border border-wt-border hover:border-wt-gold/50 hover:bg-wt-paper transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-wt-text-primary">{template.name}</span>
                      <Badge className="bg-green-500/20 text-green-400 text-xs">
                        {template.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-wt-text-muted line-clamp-2">
                      {template.components?.find(c => c.type === 'BODY')?.text || 'No preview'}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-wt-text-primary">{selectedTemplate.name}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => { setSelectedTemplate(null); setTemplateParams([]); }}
                    className="text-wt-text-muted"
                  >
                    Change
                  </Button>
                </div>
                
                <div className="p-3 bg-wt-paper rounded-lg border border-wt-border">
                  <p className="text-sm text-wt-text-primary whitespace-pre-wrap">
                    {(() => {
                      let body = selectedTemplate.components?.find(c => c.type === 'BODY')?.text || '';
                      templateParams.forEach((param, i) => {
                        body = body.replace(`{{${i + 1}}}`, param || `[Variable ${i + 1}]`);
                      });
                      return body;
                    })()}
                  </p>
                </div>
                
                {templateParams.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-wt-text-primary">Fill in variables:</p>
                    {(() => {
                      const body = selectedTemplate.components?.find(c => c.type === 'BODY')?.text || '';
                      const bodyVarCount = (body.match(/\{\{\d+\}\}/g) || []).length;
                      const buttonsComponent = selectedTemplate.components?.find(c => c.type === 'BUTTONS');
                      const buttonUrlVars = [];
                      
                      if (buttonsComponent?.buttons) {
                        buttonsComponent.buttons.forEach((btn, idx) => {
                          if (btn.type === 'URL' && btn.url?.includes('{{')) {
                            buttonUrlVars.push({ idx, text: btn.text, baseUrl: btn.url.split('{{')[0] });
                          }
                        });
                      }
                      
                      return (
                        <>
                          {/* Body Variables */}
                          {bodyVarCount > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs text-wt-text-muted">Message Variables</p>
                              {templateParams.slice(0, bodyVarCount).map((param, idx) => (
                                <div key={`body-${idx}`}>
                                  <label className="text-xs text-wt-text-muted mb-1 block">
                                    {`{{${idx + 1}}}`}
                                  </label>
                                  <Input
                                    value={param}
                                    onChange={(e) => {
                                      const newParams = [...templateParams];
                                      newParams[idx] = e.target.value;
                                      setTemplateParams(newParams);
                                    }}
                                    placeholder={`Enter value for variable ${idx + 1}`}
                                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Button URL Variables */}
                          {buttonUrlVars.length > 0 && (
                            <div className="space-y-2 pt-2 border-t border-wt-border">
                              <p className="text-xs text-wt-text-muted">Button URLs</p>
                              {buttonUrlVars.map((btn, btnIdx) => (
                                <div key={`btn-${btn.idx}`}>
                                  <label className="text-xs text-wt-text-muted mb-1 block">
                                    Button: "{btn.text}"
                                    <span className="text-wt-text-muted ml-2 font-mono">{btn.baseUrl}...</span>
                                  </label>
                                  <Input
                                    value={templateParams[bodyVarCount + btnIdx] || ''}
                                    onChange={(e) => {
                                      const newParams = [...templateParams];
                                      newParams[bodyVarCount + btnIdx] = e.target.value;
                                      setTemplateParams(newParams);
                                    }}
                                    placeholder="Enter URL suffix (e.g., /product/123)"
                                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
                
                <div className="flex justify-end gap-3 pt-4 border-t border-wt-border">
                  <Button 
                    variant="ghost" 
                    onClick={() => setShowTemplateDialog(false)}
                    className="text-wt-text-secondary"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSendTemplate}
                    disabled={sending || templateParams.some(p => !p.trim())}
                    className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                  >
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send Template
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Compose Email Dialog */}
      <Dialog open={composeDialogOpen} onOpenChange={setComposeDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-400" />
              Compose New Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {emailAccounts.length > 1 && (
              <div>
                <Label className="text-wt-text-muted text-xs">From</Label>
                <select
                  value={selectedEmailAccount || ''}
                  onChange={e => setSelectedEmailAccount(e.target.value)}
                  className="w-full h-9 rounded-md border border-wt-border bg-wt-paper text-wt-text-primary px-3 text-sm"
                >
                  {emailAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.account_name} ({acc.email_address})</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <Label className="text-wt-text-muted text-xs">To</Label>
              <Input
                value={composeData.to}
                onChange={e => setComposeData(p => ({...p, to: e.target.value}))}
                placeholder="recipient@example.com"
                className="bg-wt-paper border-wt-border text-wt-text-primary"
                data-testid="compose-to-input"
              />
            </div>
            <div>
              <Label className="text-wt-text-muted text-xs">Cc (optional)</Label>
              <Input
                value={composeData.cc}
                onChange={e => setComposeData(p => ({...p, cc: e.target.value}))}
                placeholder="cc@example.com (comma-separated)"
                className="bg-wt-paper border-wt-border text-wt-text-primary"
              />
            </div>
            <div>
              <Label className="text-wt-text-muted text-xs">Subject</Label>
              <Input
                value={composeData.subject}
                onChange={e => setComposeData(p => ({...p, subject: e.target.value}))}
                placeholder="Email subject"
                className="bg-wt-paper border-wt-border text-wt-text-primary"
                data-testid="compose-subject-input"
              />
            </div>
            <div>
              <Label className="text-wt-text-muted text-xs">Message</Label>
              <Textarea
                value={composeData.body}
                onChange={e => setComposeData(p => ({...p, body: e.target.value}))}
                placeholder="Type your email message..."
                className="bg-wt-paper border-wt-border text-wt-text-primary min-h-[200px]"
                data-testid="compose-body-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setComposeDialogOpen(false)} className="border-wt-border text-wt-text-primary">
              Cancel
            </Button>
            <Button
              onClick={handleComposeNewEmail}
              disabled={composeSending}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
              data-testid="compose-send-btn"
            >
              {composeSending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InboxPage;
